/**
 * BD DETAILS FORM - PRODUCTION READY
 * Equipment ? Category, ICR ? Inverter cascading
 * Auto-populated Budget PR, proper data loading/saving
 * @version 2.0.0
 */

import React, { useState, useRef, useMemo, useEffect, useLayoutEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  UniversalTable,
  useTableData,
  useTableNavigation,
  useAutoScroll,
  DEFAULT_STYLE_CONFIG,
  EXPANDED_STYLE_CONFIG
} from '../../../universal_table';
import { usePersistentTableData } from '../../../universal_table/hooks/usePersistentTableData';
import { FormActions, StatusBanner, SummaryModal, CorrectMistakeModal, DateFilter, DateRangeFilter } from '../../../shared';
import {
  BD_DETAILS_COLUMNS,
  getCurrentMonthPR,
  getCategoriesForEquipment,
  getInvertersForICR
} from '../forms_data/bdDetailsColumns';
import { bdDetailsService, BDDetailsDataRow, loadAllPaginatedData } from '../services/axonApiService';
import { useAuth } from '../../../../../../context/AuthContext';
import backendApi from '../../../../../../services/backendApi';
import { resolveUserFilterId, shouldLoadAllRecords } from '../utils/userDataAccess';
import { buildExcelUploadStorageKey, buildUserScopedStorageKey } from '../utils/storageKeys';
import { buildGenerationReportFooterText } from '../utils/generationReportFooter';
import { addRemovedRow, filterRemovedRows, syncRemovedRowsWithBackend, removeRemovedRow, clearRemovedRows } from '../utils/removedRowsManager';
import { Edit, ChevronUp, Plus } from 'lucide-react';
import { DeleteRowDialog } from '../components/DeleteRowDialog';

interface BDDetailsFormProps {
  site?: string;
}

export const BDDetailsForm: React.FC<BDDetailsFormProps> = ({ site: siteProp }) => {
  const { site: siteFromParams } = useParams<{ site: string }>();
  const site = siteProp || siteFromParams;
  const siteCode = (site || 'axon').toLowerCase();
  const [isCondensed, setIsCondensed] = useState(true);
  const [status, setStatus] = useState<any>({ type: null, message: '' });
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState<BDDetailsDataRow[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [showCorrectMistake, setShowCorrectMistake] = useState(false);
  const [correctMistakeData, setCorrectMistakeData] = useState<BDDetailsDataRow[]>([]);
  const [loadingCorrectMistake, setLoadingCorrectMistake] = useState(false);
  const [siteName, setSiteName] = useState<string>('axon');
  // Range Filter State
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const { user } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<{ index: number; key: string } | null>(null);
  const [totalCountFromDB, setTotalCountFromDB] = useState<number | null>(null); // Total count from database
  const [isDateFilterLoading, setIsDateFilterLoading] = useState(false); // Loading state for date filter
  const [dateFilterProgress, setDateFilterProgress] = useState<string>(''); // Progress message for date filter

  // ? Debug flag - must be declared before any usage
  const BD_DEBUG = false; // Set true for debug logging, false for production speed

  const [equipmentMappings, setEquipmentMappings] = useState<Record<string, string[]>>({});

  // Fetch equipment-fault category mappings from backend
  useEffect(() => {
    const fetchMappings = async () => {
      try {
        const response = await backendApi.makeRequest('/v1/equipment-fault-category-mappings/');
        if (response) {
          setEquipmentMappings(response);
          BD_DEBUG && console.log('Successfully fetched equipment mappings from backend:', Object.keys(response));
        }
      } catch (error) {
        console.error('Failed to fetch equipment fault category mappings from backend, using fallback:', error);
      }
    };
    fetchMappings();
  }, []);

  // Dynamic columns for Correct Mistake Modal - update options based on row data
  const modalColumns = useMemo(() => {
    return BD_DETAILS_COLUMNS.map(col => {
      // For BD Category, get options from all rows' Equipment values
      if (col.key === 'BD Category') {
        const equipmentValues = [...new Set(correctMistakeData.map((r: any) => r['Equipment']).filter(Boolean))];
        const allCategories = new Set<string>();
        equipmentValues.forEach((eq: string) => {
          const cats = equipmentMappings[eq] || getCategoriesForEquipment(eq);
          cats.forEach(cat => allCategories.add(cat));
        });
        // Also include any existing BD Category values from the data (in case they're not in the options)
        correctMistakeData.forEach((r: any) => {
          if (r['BD Category']) allCategories.add(r['BD Category']);
        });
        return {
          ...col,
          options: Array.from(allCategories)
        };
      }

      // For Equipment, update options dynamically from backend if available
      if (col.key === 'Equipment') {
        const keys = Object.keys(equipmentMappings);
        return {
          ...col,
          options: keys.length > 0 ? keys : col.options
        };
      }

      // For Inverter No., get options from all rows' ICR values
      if (col.key === 'Inverter No.') {
        const icrValues = [...new Set(correctMistakeData.map((r: any) => r['ICR No']).filter(Boolean))];
        const allInverters = new Set<string>();
        icrValues.forEach((icr: string) => {
          getInvertersForICR(icr).forEach(inv => allInverters.add(inv));
        });
        // Also include any existing Inverter No. values from the data (in case they're not in the options)
        correctMistakeData.forEach((r: any) => {
          if (r['Inverter No.']) allInverters.add(r['Inverter No.']);
        });
        return {
          ...col,
          options: Array.from(allInverters)
        };
      }

      return col;
    });
  }, [correctMistakeData, equipmentMappings]);


  const containerRef = useRef<HTMLDivElement>(null);
  const isManuallySortingRef = useRef(false);
  const [tableKey, setTableKey] = useState(0); // Force re-render key
  const previousRowKeysRef = useRef<string[]>([]); // Track previous rowKeys to detect changes
  const justAddedRowRef = useRef<string | null>(null); // Track newly added row to prevent real-time refresh from overwriting
  const hasScrolledToBottomRef = useRef(false); // Track if we've scrolled to bottom on initial load
  const [isTableVisible, setIsTableVisible] = useState(false); // Hide table until scroll is positioned
  const cursorDateRef = useRef<string | null>(null); // API pagination cursor (date)
  const cursorIdRef = useRef<string | null>(null); // API pagination cursor (id)
  const lastScrollTopRef = useRef<number>(-1); // Track last scroll position for scroll direction detection // Track last scroll position for scroll direction detection
  const isLoadingMoreRef = useRef(false); // Track if we're currently loading more data
  const hasMoreDataRef = useRef(true); // Track if there's more data to load
  const [hasMoreData, setHasMoreData] = useState(true); // State version for UI updates
  const allLoadedDataRef = useRef<Record<string, any>>({}); // Store ALL loaded data (not just visible)
  const allLoadedKeysRef = useRef<string[]>([]); // Store ALL loaded keys (not just visible)
  // Scroll restore ref: set before prepending rows, consumed by useLayoutEffect
  const scrollRestoreRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);

  // ? PERF: Refs that always hold the latest data/rowKeys to avoid stale closures
  // without putting data/rowKeys in useCallback deps (which causes cascade re-renders)
  const dataRef = useRef<Record<string, any>>({});
  const rowKeysRef = useRef<string[]>([]);

  const persistentStorageKey = useMemo(
    () => buildUserScopedStorageKey('unsaved_bdDetails', siteCode, user),
    [siteCode, user?.username, user?.email, user?.fullName, user?.siteId]
  );

  // IMPORTANT: Allow persisted data from all dates (no date restrictions)
  // Data will be loaded from backend anyway, so persisted data is just for unsaved changes

  // ? PERFORMANCE: Disable localStorage persistence for BD Details
  // With 6800+ rows, JSON.stringify + localStorage.setItem blocks main thread for seconds
  // Data is always loaded fresh from backend API, so persistence is unnecessary
  const { data, rowKeys: hookRowKeys, updateCell, removeRow, setAllData } = usePersistentTableData(
    {},
    [],
    { storageKey: persistentStorageKey, enabled: false, debounceMs: 500 }
  );
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

  // Track tab visibility to gate polling/refresh work
  useEffect(() => {
    const handleVisibility = () => setIsTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Local state to track rowKeys for immediate updates
  const [rowKeys, setRowKeysLocal] = useState<string[]>(hookRowKeys);

  const footerText = useMemo(
    () => buildGenerationReportFooterText({
      siteName,
      sheetName: 'Breakdowns',
      dateKeys: rowKeys
    }),
    [siteName, rowKeys]
  );

  // ? PERF: Keep refs in sync with state for use in callbacks
  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { rowKeysRef.current = rowKeys; }, [rowKeys]);

  // Filter removed rows whenever data is restored from hook (e.g., when switching sheets)
  // This runs after initial load to catch data restored from localStorage
  const lastFilteredSignatureRef = useRef<string>('');
  const previousSiteRef = useRef<string>(siteCode);

  // Reset filter signature when site changes (switching between different sites)
  useEffect(() => {
    if (previousSiteRef.current !== siteCode) {
      previousSiteRef.current = siteCode;
      lastFilteredSignatureRef.current = '';
      BD_DEBUG && console.log(`?? Site changed to ${siteCode}, resetting filter signature`);
    }
  }, [siteCode]);

  useEffect(() => {
    // IMPORTANT: Allow all dates in persisted data (no date restrictions)
    // Data will be loaded from backend anyway

    // Skip during initial load - that's handled in the initial load effect
    if (isInitialLoad) {
      lastFilteredSignatureRef.current = '';
      return;
    }

    if (hookRowKeys.length === 0) {
      lastFilteredSignatureRef.current = '';
      return;
    }

    // Create a signature for this data set to avoid filtering the same data multiple times
    const dataSignature = `${hookRowKeys.length}_${hookRowKeys[0] || ''}_${hookRowKeys[hookRowKeys.length - 1] || ''}`;
    if (lastFilteredSignatureRef.current === dataSignature) {
      return; // Already filtered this data set
    }

    // Check if we need to filter removed rows
    const filterData = async () => {
      try {
        const userResponse = await backendApi.getCurrentUser();
        let userIdForFilter: number | undefined;
        if (userResponse.success && userResponse.user?.id) {
          const backendUser = userResponse.user;
          const resolvedUserId = resolveUserFilterId(user, backendUser);
          userIdForFilter = resolvedUserId ?? Number(backendUser.id);
        }

        const { data: filteredData, rowKeys: filteredKeys } = filterRemovedRows(
          data,
          hookRowKeys,
          'BD Details',
          siteCode,
          userIdForFilter
        );

        // Create signature for filtered data
        const filteredSignature = `${filteredKeys.length}_${filteredKeys[0] || ''}_${filteredKeys[filteredKeys.length - 1] || ''}`;

        // Only update if filtering removed any rows
        if (filteredKeys.length !== hookRowKeys.length || JSON.stringify(filteredKeys) !== JSON.stringify(hookRowKeys)) {
          BD_DEBUG && console.log(`?? Filtering restored data: ${hookRowKeys.length} -> ${filteredKeys.length} rows (${hookRowKeys.length - filteredKeys.length} removed)`);
          lastFilteredSignatureRef.current = filteredSignature;

          // Update cursorDateRef after filtering
          if (filteredKeys.length > 0) {
            const sortedAfterFilter = sortKeysByDateValue(filteredKeys, filteredData);
            updateOldestLoadedRefs(sortedAfterFilter[0], filteredData);
          }

          setAllData(filteredData, filteredKeys);
        } else {
          lastFilteredSignatureRef.current = dataSignature;
        }
      } catch (error) {
        BD_DEBUG && console.warn('Failed to filter restored data:', error);
        lastFilteredSignatureRef.current = dataSignature;
      }
    };

    // Use a small delay to ensure data is fully loaded
    const timeoutId = setTimeout(filterData, 100);
    return () => clearTimeout(timeoutId);
  }, [hookRowKeys.length, hookRowKeys[0], siteCode, isInitialLoad]);

  // Sync local state with hook state (except when manually sorting)
  useEffect(() => {
    if (!isManuallySortingRef.current) {
      // Only sync if the order actually changed (not just reference)
      // Lightweight order check: compare length + first/last keys
      const orderChanged = hookRowKeys.length !== rowKeys.length ||
        hookRowKeys[0] !== rowKeys[0] ||
        hookRowKeys[hookRowKeys.length - 1] !== rowKeys[rowKeys.length - 1];
      if (orderChanged) {
        BD_DEBUG && console.log('?? Syncing rowKeys from hook:', hookRowKeys.length, 'keys');
        BD_DEBUG && console.log('?? Hook rowKeys sample:', hookRowKeys.slice(0, 5));
        BD_DEBUG && console.log('?? Current rowKeys sample:', rowKeys.slice(0, 5));
        setRowKeysLocal(hookRowKeys);
        previousRowKeysRef.current = hookRowKeys;
      }
    }
  }, [hookRowKeys]);

  // Debug: Log when data or rowKeys change significantly
  useEffect(() => {
    if (!isInitialLoad && rowKeys.length > 0) {
      const dataKeys = Object.keys(data);
      BD_DEBUG && console.log(`?? [DATA UPDATE] rowKeys.length=${rowKeys.length}, data.keys=${dataKeys.length}, match=${rowKeys.length === dataKeys.length}`);
      if (rowKeys.length !== dataKeys.length) {
        BD_DEBUG && console.warn(`?? [DATA UPDATE] Mismatch detected! Missing keys:`, rowKeys.filter(k => !data[k]).slice(0, 5));
      }
    }
  }, [rowKeys.length, isInitialLoad]);

  // CRITICAL: Ensure cursorDateRef is always set when rowKeys change
  useEffect(() => {
    if (isInitialLoad || rowKeys.length === 0) {
      return;
    }

    // Update cursorDateRef whenever rowKeys change
    const sortedKeys = sortKeysByDateValue(rowKeys, data);
    if (sortedKeys.length > 0) {
      const oldestDate = getDateForKey(sortedKeys[0], data);
      if (oldestDate && oldestDate !== cursorDateRef.current) {
        updateOldestLoadedRefs(sortedKeys[0], data);
      }
    }
  }, [rowKeys.length, isInitialLoad]);

  // Track rowKeys changes and force re-render if order changed
  useEffect(() => {
    if (isInitialLoad || rowKeys.length === 0) {
      previousRowKeysRef.current = rowKeys;
      return;
    }

    // Lightweight check: compare length + first/last keys instead of full JSON.stringify
    const prev = previousRowKeysRef.current;
    const orderChanged = rowKeys.length !== prev.length ||
      rowKeys[0] !== prev[0] ||
      rowKeys[rowKeys.length - 1] !== prev[prev.length - 1];
    if (orderChanged && !isManuallySortingRef.current) {
      BD_DEBUG && console.log('?? Detected rowKeys order change, forcing re-render');
      setTableKey(prev => prev + 1);
    }
    previousRowKeysRef.current = rowKeys;
  }, [rowKeys, isInitialLoad]);

  // Memoize rowKeys to ensure new reference when order changes
  // Use tableKey to force update when sorting occurs

  const memoizedRowKeys = useMemo(() => Array.from(rowKeys), [rowKeys]);

  // Filter rowKeys by selected date range
  const filteredRowKeys = useMemo(() => {
    if (!startDate && !endDate) return rowKeys;

    return rowKeys.filter(key => {
      // Extract date from key or column
      const rowData = data[key];
      let rowDateString = '';
      if (rowData?.entry_date) rowDateString = String(rowData.entry_date);
      else if (rowData?.date) rowDateString = String(rowData.date);
      else rowDateString = key.split('_')[0]; // Fallback to key

      const rowDate = rowDateString.split('T')[0];

      // If only start date is selected
      if (startDate && !endDate) {
        return rowDate >= startDate;
      }

      // If only end date is selected
      if (!startDate && endDate) {
        return rowDate <= endDate;
      }

      // If both are selected
      if (startDate && endDate) {
        return rowDate >= startDate && rowDate <= endDate;
      }

      return true;
    });
  }, [rowKeys, data, startDate, endDate]);

  // DATE FILTERING: Load data from backend in chunks when date range changes
  useEffect(() => {
    if (!startDate && !endDate) return;
    if (isInitialLoad) return;

    let isCancelled = false;

    const loadFilteredDataInChunks = async () => {
      setIsDateFilterLoading(true);
      setDateFilterProgress('Initializing...');

      try {
        // Get current user
        const userResponse = await backendApi.getCurrentUser();
        if (!userResponse.success || !userResponse.user?.id || isCancelled) {
          setIsDateFilterLoading(false);
          return;
        }

        const backendUser = userResponse.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;

        const fetchRangeAllRows = async (rangeStart: string, rangeEnd: string) => {
          return loadAllPaginatedData(
            (params) => bdDetailsService.loadByDateRange({
              userId: userIdForLoad,
              siteCode: siteCode,
              siteId: user?.siteId || backendUser?.site?.site_id || null,
              startDate: params.startDate,
              endDate: params.endDate,
              offset: params.offset,
              limit: params.limit
            }) as any,
            {
              startDate: rangeStart,
              endDate: rangeEnd,
              initialLimit: 200
            }
          );
        };

        BD_DEBUG && console.log(`?? [BD] Loading filtered data in chunks: ${startDate} to ${endDate}`);

        // Calculate date range
        const start = new Date(startDate || new Date());
        const end = new Date(endDate || new Date());
        const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

        // If range is small (< 90 days), load in one go
        if (daysDiff <= 90) {
          setDateFilterProgress('Loading data...');
          const rows = await fetchRangeAllRows(startDate || '', endDate || '');

          if (isCancelled) return;

          if (rows.length > 0) {
            let loadedData: any = {};
            let loadedKeys: string[] = [];
            const keyUsageCount: Record<string, number> = {}; // Support database duplicates

            // Use optimized mapping helper
            rows.forEach((row: any, idx: number) => {
              const mapped = mapBackendRowToFrontend(row, idx);
              if (!mapped.key || !mapped.data) return;

              // Support duplicates with unique key suffixes
              let finalKey = mapped.key;
              if (loadedData[finalKey]) {
                keyUsageCount[mapped.key] = (keyUsageCount[mapped.key] || 0) + 1;
                finalKey = `${mapped.key}__dup${keyUsageCount[mapped.key]}`;
              }

              loadedData[finalKey] = mapped.data;
              loadedKeys.push(finalKey);
            });

            setAllData(loadedData, loadedKeys.sort());
            BD_DEBUG && console.log(`? [BD] Loaded ${loadedKeys.length} filtered records`);
          }
        } else {
          // Load in chunks for large date ranges
          let allData: any = {};
          let allKeys: string[] = [];
          // Support multiple BD entries on the same date (composite key already includes
          // the backend row id, so a collision here means a genuinely different row that
          // fell back to a temp key) — same rescue used by the <90-day branch above and
          // by the initial-load/lazy-load-more paths elsewhere in this file. Without this,
          // same-date duplicate rows were silently dropped when a date filter spanned
          // more than 90 days.
          const keyUsageCount: Record<string, number> = {};
          let currentStart = new Date(start);
          let chunkNum = 0;
          const totalChunks = Math.ceil(daysDiff / DATE_FILTER_CHUNK_SIZE);

          while (currentStart <= end) {
            if (isCancelled) return;
            chunkNum++;
            const chunkEnd = new Date(currentStart);
            chunkEnd.setDate(chunkEnd.getDate() + DATE_FILTER_CHUNK_SIZE - 1);
            if (chunkEnd > end) chunkEnd.setTime(end.getTime());

            const chunkStartStr = currentStart.toISOString().split('T')[0];
            const chunkEndStr = chunkEnd.toISOString().split('T')[0];

            setDateFilterProgress(`Loading chunk ${chunkNum}/${totalChunks} (${chunkStartStr} to ${chunkEndStr})...`);
            BD_DEBUG && console.log(`?? [BD] Chunk ${chunkNum}/${totalChunks}: ${chunkStartStr} to ${chunkEndStr}`);

            const rows = await fetchRangeAllRows(chunkStartStr, chunkEndStr);

            if (isCancelled) return;

            if (rows.length > 0) {
              // Use optimized mapping helper
              rows.forEach((row: any, idx: number) => {
                const mapped = mapBackendRowToFrontend(row, Object.keys(allData).length + idx);
                if (!mapped.key || !mapped.data) return;

                // Support duplicates with unique key suffixes (same pattern as the
                // <90-day branch above)
                let finalKey = mapped.key;
                if (allData[finalKey]) {
                  keyUsageCount[mapped.key] = (keyUsageCount[mapped.key] || 0) + 1;
                  finalKey = `${mapped.key}__dup${keyUsageCount[mapped.key]}`;
                }

                allData[finalKey] = mapped.data;
                allKeys.push(finalKey);
              });

              // Update UI progressively every 2 chunks
              if (chunkNum % 2 === 0 || chunkNum === totalChunks) {
                setAllData(allData, allKeys.sort());
              }
            }

            currentStart.setDate(currentStart.getDate() + DATE_FILTER_CHUNK_SIZE);

            // Small delay to prevent UI freeze
            await new Promise(resolve => setTimeout(resolve, 50));
          }

          setAllData(allData, allKeys.sort());
          BD_DEBUG && console.log(`? [BD] Loaded ${allKeys.length} filtered records in ${chunkNum} chunks`);
        }
      } catch (error) {
        console.error('? [BD] Error loading filtered data:', error);
        setStatus({ type: 'error', message: 'Failed to load filtered data. Please try again.' });
      } finally {
        setIsDateFilterLoading(false);
        setDateFilterProgress('');
      }
    };

    loadFilteredDataInChunks();
    return () => {
      isCancelled = true;
    };
  }, [startDate, endDate]);

  const { scrollToCell, registerInput } = useAutoScroll(containerRef);
  const { focusedCell, setFocusedCell, handleKeyDown } = useTableNavigation(
    rowKeys.length,
    BD_DETAILS_COLUMNS.length,
    BD_DETAILS_COLUMNS,
    scrollToCell
  );
  // Stable reference so memoized table rows aren't forced to re-render just because
  // this closure was recreated (setFocusedCell itself is already stable).
  const handleTableFocus = useCallback(
    (r: number, c: number) => setFocusedCell({ rowIndex: r, colIndex: c }),
    [setFocusedCell]
  );

  // Fetch site name from database
  useEffect(() => {
    const fetchSiteName = async () => {
      try {
        const response = await backendApi.getSites(false, siteCode.toUpperCase());
        if (response.success && Array.isArray(response.sites)) {
          const matchedSite = response.sites.find(
            (item: any) => item.site_code && item.site_code.toUpperCase() === siteCode.toUpperCase()
          ) || response.sites[0];

          if (matchedSite && matchedSite.site_name) {
            setSiteName(matchedSite.site_name);
          } else {
            setSiteName(siteCode.toUpperCase());
          }
        }
      } catch (error) {
        BD_DEBUG && console.warn('Failed to fetch site name:', error);
        setSiteName(siteCode.toUpperCase());
      }
    };

    fetchSiteName();
  }, [siteCode]);

  /**
   * Create empty row with all fields initialized
   */
  const createEmptyRow = (date: string) => {
    return {
      date: date,
      'Budget PR for the month': getCurrentMonthPR(),
      'Equipment': '',
      'BD Category': '',
      'ICR No': '',
      'Inverter No.': '',
      'BD From': '',
      'BD up  to': '',
      'BD Minutes': '',
      'BD Hours': '',
      'PA BD Hours': '',
      'A = DC Capacity of affected equipment (kW)': '',
      'B = POA lost during BD (kWh/m2)': '',
      'C=A*B': '',
      'Generation loss kWh': '',
      "SCB's /QS/UNIT": '',
      'BD Description': '',
      'Corrective Action Taken': '',
      'Current Status': '',
      'Effective loss of POA': '',
      'Scope': '',
      'Which Equipment is in off condition': '',
      'Cause (Error Code)': ''
    };
  };

  /**
   * Sorting helpers to keep rows grouped by their actual date value
   */
  const normalizeDateForSort = (value: any): string => {
    if (!value) return '';
    const str = String(value).trim();
    // Already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const parsed = new Date(str);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
    return str; // Fallback to raw string compare
  };

  const getDateForKey = (key: string, dataMap: Record<string, any>): string => {
    const rowDate = dataMap[key]?.date;
    if (rowDate) return normalizeDateForSort(rowDate);
    const keyDate = key.includes('_') ? key.split('_')[0] : key;
    return normalizeDateForSort(keyDate);
  };

  const getRowIdForSort = (key: string, dataMap: Record<string, any>): number => {
    const rowId = dataMap[key]?.id;
    if (rowId !== undefined && rowId !== null && !isNaN(Number(rowId))) {
      return Number(rowId);
    }
    const idFromKey = key.includes('_') ? parseInt(key.split('_')[1] || '0', 10) : 0;
    return isNaN(idFromKey) ? 0 : idFromKey;
  };

  /**
   * Update oldest loaded refs (date and ID) from the oldest key
   * This ensures ID-based pagination works correctly for multiple rows per date
   */
  const updateOldestLoadedRefs = (oldestKey: string, dataMap: Record<string, any>) => {
    const oldestDate = getDateForKey(oldestKey, dataMap);
    const oldestId = getRowIdForSort(oldestKey, dataMap);

    cursorDateRef.current = oldestDate;
    cursorIdRef.current = oldestId > 0 ? oldestId.toString() : null;

    BD_DEBUG && console.log(`?? [UPDATE OLDEST] Updated cursorDateRef: ${oldestDate}, cursorIdRef: ${oldestId}`);
  };

  const sortKeysByDateValue = (keys: string[], dataMap: Record<string, any>) => {
    return [...keys].sort((a, b) => {
      const dateA = getDateForKey(a, dataMap);
      const dateB = getDateForKey(b, dataMap);
      // Sort by date ascending (oldest first, newest last)
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      // For same date, sort by ID ascending to keep rows grouped together
      // This ensures all rows with same date appear together
      const idA = getRowIdForSort(a, dataMap);
      const idB = getRowIdForSort(b, dataMap);
      return idA - idB; // Ascending ID order for same date
    });
  };

  const ordersMatch = (a: string[], b: string[]) => JSON.stringify(a) === JSON.stringify(b);

  // ? SUPERFAST LOADING CONSTANTS
  const INITIAL_LOAD_DAYS = 730; // 2 years - ensures ALL data found on FIRST API call (no fallback needed)
  const INITIAL_MAX_ROWS = 200; // Maximum rows for Excel import and fallback
  const ROWS_PER_SCROLL_LOAD = 50; // Rows to load when scrolling up
  const LOAD_MORE_DAYS = 30; // Days to look back when loading older data
  const MAX_ROWS_PER_CHUNK = 500; // Maximum rows per chunk for date filtering
  const DATE_FILTER_CHUNK_SIZE = 60; // Load 60 days at a time when date filtering

  /**
   * MAP BACKEND DATA TO FRONTEND FORMAT
   * Efficiently converts backend row to frontend format
   * Pre-formatted mapping reduces frontend processing time
   */
  const mapBackendRowToFrontend = (row: any, index: number = 0): { key: string; data: any } => {
    const entryDate = normalizeDateForSort(row.entry_date);
    if (!entryDate) return { key: '', data: null };

    const rowId = row.id || row.pk;
    const compositeKey = rowId ? `${entryDate}_${rowId}` : `${entryDate}_temp${index}`;

    return {
      key: compositeKey,
      data: {
        id: rowId,
        date: entryDate,
        'Budget PR for the month': row.budget_pr_for_month || getCurrentMonthPR(),
        'Equipment': row.equipment || '',
        'BD Category': row.bd_category || '',
        'ICR No': row.icr_no || '',
        'Inverter No.': row.inverter_no || '',
        'BD From': row.bd_from || '',
        'BD up  to': row.bd_up_to || '',
        'BD Minutes': row.bd_minutes !== null && row.bd_minutes !== undefined ? String(row.bd_minutes) : '',
        'BD Hours': row.bd_hours !== null && row.bd_hours !== undefined ? String(row.bd_hours) : '',
        'PA BD Hours': row.pa_bd_hours !== null && row.pa_bd_hours !== undefined ? String(row.pa_bd_hours) : '',
        'A = DC Capacity of affected equipment (kW)': row.dc_capacity_kw !== null && row.dc_capacity_kw !== undefined ? String(row.dc_capacity_kw) : '',
        'B = POA lost during BD (kWh/m2)': row.poa_lost_kwh_per_m2 !== null && row.poa_lost_kwh_per_m2 !== undefined ? String(row.poa_lost_kwh_per_m2) : '',
        'C=A*B': row.c_a_times_b !== null && row.c_a_times_b !== undefined ? String(row.c_a_times_b) : '',
        'Generation loss kWh': row.generation_loss_kwh !== null && row.generation_loss_kwh !== undefined ? String(row.generation_loss_kwh) : '',
        "SCB's /QS/UNIT": row.scb_qs_unit || '',
        'BD Description': row.bd_description || '',
        'Corrective Action Taken': row.corrective_action_taken || '',
        'Current Status': row.current_status || '',
        'Effective loss of POA': row.effective_loss_of_poa || '',
        'Scope': row.scope || '',
        'Which Equipment is in off condition': row.equipment_off_condition || '',
        'Cause (Error Code)': row.cause_error_code || ''
      }
    };
  };

  const makeUniqueRowKey = (
    baseKey: string,
    dataMap: Record<string, any>,
    keys: string[]
  ): string => {
    if (!dataMap[baseKey] && !keys.includes(baseKey)) {
      return baseKey;
    }

    let counter = 1;
    let candidate = `${baseKey}__dup${counter}`;
    while (dataMap[candidate] || keys.includes(candidate)) {
      counter += 1;
      candidate = `${baseKey}__dup${counter}`;
    }
    return candidate;
  };

  /**
   * Verify if a row with given date and ID already exists in the data
   * This ensures proper duplicate detection using both date and ID
   */
  const rowExists = (date: string, id: number | string | null, currentData: Record<string, any>, currentKeys: string[]): boolean => {
    if (!date) return false;

    const normalizedDate = normalizeDateForSort(date);
    const numericId = id ? Number(id) : null;

    // Check if composite key exists
    const compositeKey = numericId !== null ? `${normalizedDate}_${numericId}` : normalizedDate;
    if (currentKeys.includes(compositeKey) && currentData[compositeKey]) {
      return true;
    }

    // Also check by verifying date and ID in existing data
    for (const key of currentKeys) {
      const existingRow = currentData[key];
      if (existingRow) {
        const existingDate = normalizeDateForSort(existingRow.date || key.split('_')[0]);
        const existingId = existingRow.id !== undefined && existingRow.id !== null ? Number(existingRow.id) : null;

        // Match if date and ID both match
        if (existingDate === normalizedDate && existingId === numericId) {
          return true;
        }
      }
    }

    return false;
  };

  /**
   * Keep only the most recent dates/rows to avoid loading huge datasets at once.
   */
  const clampRecentData = (
    dataMap: Record<string, any>,
    keys: string[],
    maxDates = 100,
    maxRows = 2000
  ) => {
    const sortedKeys = sortKeysByDateValue(keys, dataMap);
    const uniqueDates = Array.from(new Set(sortedKeys.map(k => getDateForKey(k, dataMap)))).sort();
    const keepDates = uniqueDates.slice(-maxDates);
    const filteredKeys = sortedKeys.filter(k => keepDates.includes(getDateForKey(k, dataMap)));
    const limitedKeys = filteredKeys.slice(-maxRows); // keep newest within the kept dates
    const filteredData: Record<string, any> = {};
    limitedKeys.forEach(k => {
      if (dataMap[k]) filteredData[k] = dataMap[k];
    });
    return { filteredData, filteredKeys: limitedKeys };
  };

  /**
   * Dynamic columns with cascading dropdowns
   * IMPORTANT: Preserve existing selections by including all existing values in options
   */
  // Split out the expensive O(rows) scans for "every value ever used" so they only
  // recompute when the underlying data/rows actually change — NOT on every arrow-key
  // navigation (which only moves `focusedCell.rowIndex`). Previously this whole scan
  // re-ran on every keystroke AND every focus move; it's the single hottest path found
  // in the performance audit for this tab.
  const existingBdCategories = useMemo(() => {
    const set = new Set<string>();
    rowKeys.forEach(key => {
      const row = data[key];
      if (row && row['BD Category']) set.add(row['BD Category']);
    });
    return set;
  }, [data, rowKeys]);

  const existingInverterNos = useMemo(() => {
    const set = new Set<string>();
    rowKeys.forEach(key => {
      const row = data[key];
      if (row && row['Inverter No.']) set.add(row['Inverter No.']);
    });
    return set;
  }, [data, rowKeys]);

  const dynamicColumns = useMemo(() => {
    return BD_DETAILS_COLUMNS.map(col => {
      // Handle BD Category dropdown based on Equipment selection
      if (col.key === 'BD Category') {
        // Get categories for focused row's equipment
        const currentRow = data[rowKeys[focusedCell.rowIndex]] || {};
        const selectedEquipment = currentRow['Equipment'];
        const focusedCategories = selectedEquipment
          ? (equipmentMappings[selectedEquipment] || getCategoriesForEquipment(selectedEquipment))
          : [];

        // Combine focused row options with all existing values to preserve selections
        const allOptions = new Set([...focusedCategories, ...existingBdCategories]);

        return {
          ...col,
          options: Array.from(allOptions)
        };
      }

      // Handle Equipment dropdown list from dynamic backend data if available
      if (col.key === 'Equipment') {
        const keys = Object.keys(equipmentMappings);
        return {
          ...col,
          options: keys.length > 0 ? keys : col.options
        };
      }

      // Handle Inverter dropdown based on ICR selection
      if (col.key === 'Inverter No.') {
        // Get inverters for focused row's ICR
        const currentRow = data[rowKeys[focusedCell.rowIndex]] || {};
        const selectedICR = currentRow['ICR No'];
        const focusedInverters = selectedICR ? getInvertersForICR(selectedICR) : [];

        // Combine focused row options with all existing values to preserve selections
        const allOptions = new Set([...focusedInverters, ...existingInverterNos]);

        return {
          ...col,
          options: Array.from(allOptions)
        };
      }

      return col;
    });
  }, [data, rowKeys, focusedCell.rowIndex, equipmentMappings, existingBdCategories, existingInverterNos]);

  /**
   * Initialize by loading user's existing data or Excel uploaded data
   * IMPORTANT: Check for persisted unsaved data first to preserve user's work
   * OPTIMIZED: Check sessionStorage cache to prevent reloading when switching sheets
   */
  useEffect(() => {
    if (!isInitialLoad) return;

    BD_DEBUG && console.log(`?? [BD DETAILS] Starting initial load for site: ${siteCode}`);

    const loadUserData = async () => {
      try {
        BD_DEBUG && console.log(`?? [BD DETAILS] loadUserData started - isInitialLoad: ${isInitialLoad}`);

        // Clear temporary removals on initial page load so they reappear after refresh
        backendApi.getCurrentUser().then(response => {
          if (response.success && response.user?.id) {
            const backendUser = response.user;
            const resolvedUserId = resolveUserFilterId(user, backendUser);
            const userIdForStore = resolvedUserId ?? Number(backendUser.id);
            clearRemovedRows('BD Details', siteCode, userIdForStore);
            BD_DEBUG && console.log('?? Cleared temporary removals on page load for BD Details');
          } else {
            clearRemovedRows('BD Details', siteCode);
            BD_DEBUG && console.log('?? Cleared temporary removals on page load for BD Details');
          }
        }).catch(() => {
          clearRemovedRows('BD Details', siteCode);
          BD_DEBUG && console.log('?? Cleared temporary removals on page load for BD Details');
        });

        // CRITICAL: Clear any old cache that might have April data
        const cacheKey = `bdDetails_cache_${siteCode}_${user?.username || 'anonymous'}`;
        sessionStorage.removeItem(cacheKey);
        BD_DEBUG && console.log(`??? [CACHE CLEARED] Removed cached BD Details data to ensure fresh November data`);

        // CRITICAL: Clear persisted data if it has April dates
        const november2025Min = '2025-11-01';
        if (rowKeys.length > 0) {
          const hasOldDates = rowKeys.some(key => {
            const row = data[key];
            const rowDate = normalizeDateForSort(row?.date || key.split('_')[0]);
            return rowDate && rowDate < november2025Min;
          });

          if (hasOldDates) {
            BD_DEBUG && console.log(`??? [PERSISTED DATA CLEARED] Found old dates (before November 2025) in persisted data, clearing to force fresh load`);
            try {
              localStorage.removeItem(persistentStorageKey);
              setAllData({}, []); // Clear old data immediately
              // Reset rowKeys to empty
              rowKeys.length = 0;
            } catch (error) {
              BD_DEBUG && console.warn('Failed to clear persisted data:', error);
            }
          }
        }

        // DISABLED: Skip cache check - always load fresh data to ensure November dates, not cached April data
        // This ensures we always get the latest November 30 data from backend

        // First, check if persisted data was already restored by usePersistentTableData hook
        // Only use persisted data if it has actual meaningful data (not just empty rows)
        const hasMeaningfulData = rowKeys.some(key => {
          const row = data[key];
          if (!row) return false;
          // Check if row has any non-empty values (excluding date, id, and Budget PR which is auto-filled)
          return Object.keys(row).some(k =>
            k !== 'date' &&
            k !== 'id' &&
            k !== 'Budget PR for the month' &&
            row[k] !== null &&
            row[k] !== undefined &&
            row[k] !== ''
          );
        });

        // Always load from backend to ensure we have the latest data (including newly uploaded data)
        // Persisted data is mainly for unsaved changes, not for replacing backend data
        if (hasMeaningfulData) {
          BD_DEBUG && console.log(`?? Found persisted BD Details data (${rowKeys.length} rows), but will load from backend to ensure latest data`);
          // Don't return - continue to load from backend
          // The backend data will be merged with persisted data if needed
        }

        // If persisted data exists but is empty, clear it and start fresh
        if (rowKeys.length > 0) {
          BD_DEBUG && console.log(`?? Persisted BD Details data contains only empty rows, clearing and starting fresh`);
          try {
            localStorage.removeItem(persistentStorageKey);
            setAllData({}, []); // Clear empty rows
          } catch (error) {
            BD_DEBUG && console.warn('Failed to clear empty persisted data:', error);
          }
        }

        // Second, check for Excel uploaded data
        if (site) {
          const storageKey = buildExcelUploadStorageKey(site, user);
          const excelDataStr = localStorage.getItem(storageKey);

          if (excelDataStr) {
            try {
              const excelData = JSON.parse(excelDataStr);
              const bdData = excelData['BD Details'];

              if (bdData && (Array.isArray(bdData) || Object.keys(bdData).length > 0)) {
                let loadedData: any = {};
                let loadedKeys: string[] = [];
                let tempIdCounter = 1; // Temporary ID counter for Excel rows

                // Helper to add a single row from upload cache
                const addExcelRow = (rowData: any, dateHint?: string) => {
                  const dateValue = rowData?.date || dateHint || '';
                  const compositeKey = `${normalizeDateForSort(dateValue) || 'date'}_temp${tempIdCounter++}`;

                  loadedData[compositeKey] = {
                    date: dateValue,
                    'Budget PR for the month': rowData['Budget PR for the month'] || getCurrentMonthPR(),
                    'Equipment': rowData['Equipment'] || '',
                    'BD Category': rowData['BD Category'] || '',
                    'ICR No': rowData['ICR No'] || '',
                    'Inverter No.': rowData['Inverter No.'] || '',
                    'BD From': rowData['BD From'] || '',
                    'BD up  to': rowData['BD up  to'] || '',
                    'BD Minutes': rowData['BD Minutes'] || '',
                    'BD Hours': rowData['BD Hours'] || '',
                    'PA BD Hours': rowData['PA BD Hours'] || '',
                    'A = DC Capacity of affected equipment (kW)': rowData['A = DC Capacity of affected equipment (kW)'] || '',
                    'B = POA lost during BD (kWh/m2)': rowData['B = POA lost during BD (kWh/m2)'] || '',
                    'C=A*B': rowData['C=A*B'] || '',
                    'Generation loss kWh': rowData['Generation loss kWh'] || '',
                    "SCB's /QS/UNIT": rowData["SCB's /QS/UNIT"] || '',
                    'BD Description': rowData['BD Description'] || '',
                    'Corrective Action Taken': rowData['Corrective Action Taken'] || '',
                    'Current Status': rowData['Current Status'] || '',
                    'Effective loss of POA': rowData['Effective loss of POA'] || '',
                    'Scope': rowData['Scope'] || '',
                    'Which Equipment is in off condition': rowData['Which Equipment is in off condition'] || '',
                    'Cause (Error Code)': rowData['Cause (Error Code)'] || ''
                  };
                  loadedKeys.push(compositeKey);
                };

                if (Array.isArray(bdData)) {
                  // Already an array of rows (supports multiple rows per date)
                  bdData.forEach((rowData: any) => addExcelRow(rowData, rowData?.date));
                } else {
                  // Object keyed by date -> row or array of rows
                  Object.entries(bdData).forEach(([dateKey, rowData]: [string, any]) => {
                    if (Array.isArray(rowData)) {
                      rowData.forEach((rd: any) => addExcelRow(rd, dateKey));
                    } else {
                      addExcelRow(rowData, dateKey);
                    }
                  });
                }

                // CRITICAL: Filter Excel data to only include November 2025 onwards (exclude April)
                const november2025Excel = '2025-11-01';
                const filteredExcelData: any = {};
                const filteredExcelKeys: string[] = [];

                loadedKeys.forEach(key => {
                  const row = loadedData[key];
                  const rowDate = normalizeDateForSort(row?.date || key.split('_')[0]);
                  if (rowDate && rowDate >= november2025Excel) {
                    filteredExcelData[key] = row;
                    filteredExcelKeys.push(key);
                  } else {
                    BD_DEBUG && console.log(`??? [EXCEL LOAD] Filtering out old date: ${rowDate} (before November 2025)`);
                  }
                });

                loadedData = filteredExcelData;
                loadedKeys = filteredExcelKeys.slice(0, INITIAL_MAX_ROWS); // Limit to INITIAL_MAX_ROWS

                // Track oldest date from clamped keys for lazy loading
                if (loadedKeys.length > 0) {
                  const sortedForOldest = sortKeysByDateValue(loadedKeys, loadedData);
                  updateOldestLoadedRefs(sortedForOldest[0], loadedData);
                }

                // Filter out removed rows
                const userResponse = await backendApi.getCurrentUser();
                let userIdForFilter: number | undefined;
                if (userResponse.success && userResponse.user?.id) {
                  const backendUser = userResponse.user;
                  const resolvedUserId = resolveUserFilterId(user, backendUser);
                  userIdForFilter = resolvedUserId ?? Number(backendUser.id);
                }
                const { data: filteredDataExcel, rowKeys: filteredKeysExcel } = filterRemovedRows(
                  loadedData,
                  loadedKeys,
                  'BD Details',
                  siteCode,
                  userIdForFilter
                );

                setAllData(filteredDataExcel, filteredKeysExcel);
                // Allow further lazy loads after initial Excel import when we have any rows
                hasMoreDataRef.current = filteredKeysExcel.length > 0;
                setHasMoreData(filteredKeysExcel.length > 0);
                localStorage.removeItem(storageKey);
                setStatus({ type: 'success', message: `? Loaded ${filteredKeysExcel.length} rows from Excel upload (${loadedKeys.length - filteredKeysExcel.length} removed from view)` });
                BD_DEBUG && console.log(`? Loaded ${filteredKeysExcel.length} rows from Excel for BD Details (${loadedKeys.length - filteredKeysExcel.length} removed)`);
                setIsInitialLoad(false);
                return;
              }
            } catch (excelError) {
              console.error('Error parsing Excel data:', excelError);
            }
          }
        }

        // If no Excel data, load from backend
        // Get current user ID
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user?.id) {
          const backendUser = userResponse.user;
          const resolvedUserId = resolveUserFilterId(user, backendUser);
          const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;

          if (resolvedUserId === null && userIdForLoad === 0) {
            BD_DEBUG && console.warn('?? Unable to resolve BD Details user ID - defaulting to admin view (all data)');
          }

          // ? CURSOR PAGINATION: Initial load fetches latest 50 rows
          BD_DEBUG && console.log(`?? [INITIAL LOAD] Fetching latest 50 rows from API for ${userIdForLoad <= 0 ? 'admin' : `user ${userIdForLoad}`}`);

          const serviceResponse = await bdDetailsService.loadByDateRange({
            userId: userIdForLoad,
            siteCode: siteCode,
            siteId: user?.siteId || backendUser?.site?.site_id || null,
            limit: 50,
          });

          if (serviceResponse.ok) {
            const bdData = serviceResponse.data || [];
            
            // Save pagination cursors for "Load More"
            if (serviceResponse.meta) {
              cursorDateRef.current = serviceResponse.meta.next_cursor_date;
              cursorIdRef.current = serviceResponse.meta.next_cursor_id;
              hasMoreDataRef.current = serviceResponse.meta.has_more;
              setHasMoreData(serviceResponse.meta.has_more);
              setTotalCountFromDB(serviceResponse.meta.total_count || 0);
            }

            BD_DEBUG && console.log(`?? [BD] Got ${bdData.length} rows`);

            if (bdData.length === 0) {
              BD_DEBUG && console.warn(`?? [BD] No data found`);
              setStatus({ type: 'info', message: 'No data found. You can add new entries.' });
              setIsInitialLoad(false);
              return;
            }

            // ? FAST PROCESSING: Direct mapping without debug overhead
            let loadedData: any = {};
            let loadedKeys: string[] = [];
            let processedCount = 0;
            const keyUsageCount: Record<string, number> = {}; // Track duplicate keys to support database duplicates

            // OPTIMIZED: Use pre-formatted mapping helper for speed
            bdData.forEach((row: any, idx: number) => {
              const mapped = mapBackendRowToFrontend(row, idx);
              if (!mapped.key || !mapped.data) return;

              // Support database duplicates by making keys unique
              let finalKey = mapped.key;
              if (loadedData[finalKey]) {
                keyUsageCount[mapped.key] = (keyUsageCount[mapped.key] || 0) + 1;
                finalKey = `${mapped.key}__dup${keyUsageCount[mapped.key]}`;
              }

              loadedData[finalKey] = mapped.data;
              loadedKeys.push(finalKey);
              processedCount++;
            });

            BD_DEBUG && console.log(`?? [BD] Processed ${processedCount} rows`);

            // Maintain exact order from backend (latest first)
            setAllData(loadedData, loadedKeys);

            BD_DEBUG && console.log(`? [BD] Loaded ${loadedKeys.length} rows`);
          } else {
            BD_DEBUG && console.warn(`?? [BD] API error: ${serviceResponse.error || 'Unknown service error'}`);
            setStatus({ type: 'error', message: serviceResponse.error || 'Failed to load BD Details data' });
            setAllData({}, []);
            hasMoreDataRef.current = false;
            setHasMoreData(false);
          }

          setIsInitialLoad(false);
          return;
        }
      } catch (error: any) {
        console.error('? [BD] Error loading data:', error.message);
        setAllData({}, []);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadUserData();
  }, [isInitialLoad, setAllData, site]);

  /**
   * Real-time data refresh - Poll database every 30 seconds for fresh data
   * TEMPORARILY DISABLED to prevent infinite loop and wrong date range
   */
  useEffect(() => {
    // TEMPORARILY DISABLED: Real-time refresh causes infinite loop with wrong date range
    return;

    if (isInitialLoad) return; // Don't poll during initial load

    // Helper function to extract date from key (handles both simple dates and composite keys like "date_id")
    const extractDateFromKey = (key: string): string => {
      return key.includes('_') ? key.split('_')[0] : key;
    };

    // Check if any rowKeys start with the date in justAddedRowRef (for composite keys)
    const hasNewlyAddedRow = justAddedRowRef.current && rowKeys.some(key => {
      const keyDate = extractDateFromKey(key);
      return keyDate === justAddedRowRef.current;
    });

    if (hasNewlyAddedRow) {
      BD_DEBUG && console.log(`?? [REAL-TIME] Skipping refresh - new row just added for date: ${justAddedRowRef.current}`);
      return; // Don't refresh if a new row was just added
    }

    const loadFreshData = async () => {
      if (document.hidden) return;
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (!userResponse.success || !userResponse.user?.id) return;

        const backendUser = userResponse.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
        const isAdmin = shouldLoadAllRecords(user, backendUser);

        let loadedData: any = {};
        let loadedKeys: string[] = [];

        if (isAdmin) {
          try {
            const API_BASE_URL = 'http://localhost:8000/api/v1';
            const token = sessionStorage.getItem('access_token');
            const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=BD`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const payload = await response.json();
              const bdData = payload?.data?.bd || [];

              bdData.forEach((row: any) => {
                const entryDate = row.entry_date;
                const rowId = row.id || row.pk;
                if (entryDate) {
                  const compositeKey = rowId ? `${entryDate}_${rowId}` : entryDate;
                  loadedData[compositeKey] = {
                    id: rowId,
                    date: entryDate,
                    'Budget PR for the month': row.budget_pr_for_month || getCurrentMonthPR(),
                    'Equipment': row.equipment || '',
                    'BD Category': row.bd_category || '',
                    'ICR No': row.icr_no || '',
                    'Inverter No.': row.inverter_no || '',
                    'BD From': row.bd_from || '',
                    'BD up  to': row.bd_up_to || '',
                    'BD Minutes': row.bd_minutes !== null && row.bd_minutes !== undefined ? String(row.bd_minutes) : '',
                    'BD Hours': row.bd_hours !== null && row.bd_hours !== undefined ? String(row.bd_hours) : '',
                    'PA BD Hours': row.pa_bd_hours !== null && row.pa_bd_hours !== undefined ? String(row.pa_bd_hours) : '',
                    'A = DC Capacity of affected equipment (kW)': row.dc_capacity_kw !== null && row.dc_capacity_kw !== undefined ? String(row.dc_capacity_kw) : '',
                    'B = POA lost during BD (kWh/m2)': row.poa_lost_kwh_per_m2 !== null && row.poa_lost_kwh_per_m2 !== undefined ? String(row.poa_lost_kwh_per_m2) : '',
                    'C=A*B': row.c_a_times_b !== null && row.c_a_times_b !== undefined ? String(row.c_a_times_b) : '',
                    'Generation loss kWh': row.generation_loss_kwh !== null && row.generation_loss_kwh !== undefined ? String(row.generation_loss_kwh) : '',
                    "SCB's /QS/UNIT": row.scb_qs_unit || '',
                    'BD Description': row.bd_description || '',
                    'Corrective Action Taken': row.corrective_action_taken || '',
                    'Current Status': row.current_status || '',
                    'Effective loss of POA': row.effective_loss_of_poa || '',
                    'Scope': row.scope || '',
                    'Which Equipment is in off condition': row.equipment_off_condition || '',
                    'Cause (Error Code)': row.cause_error_code || ''
                  };
                  if (!loadedKeys.includes(compositeKey)) {
                    loadedKeys.push(compositeKey);
                  }
                }
              });
            }
          } catch (error) {
            console.error('Error refreshing BD Details data via get-dgr:', error);
            return;
          }
        } else {
          // CRITICAL: Don't use loadByUser - it uses wrong date range (10 years back)
          // Instead, use direct fetch with November 2025+ date range and limit
          BD_DEBUG && console.log(`?? [REAL-TIME] Using direct fetch with November 2025+ date range (not loadByUser)`);

          const API_BASE_URL = 'http://localhost:8000/api/v1';
          const token = sessionStorage.getItem('access_token');
          const headers: HeadersInit = {
            'X-SITE-CODE': siteCode.toUpperCase()
          };
          if (token) headers['Authorization'] = `Bearer ${token}`;

          const formatDate = (d: Date) => {
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, '0');
            const dd = String(d.getUTCDate()).padStart(2, '0');
            return `${y}-${m}-${dd}`;
          };

          // Fetch recent data (last 2 years) for real-time refresh
          const endDate = new Date();
          endDate.setUTCDate(endDate.getUTCDate() + 1);
          const startDate = new Date();
          startDate.setFullYear(startDate.getFullYear() - 2); // Go back 2 years

          const start = formatDate(startDate);
          const end = formatDate(endDate);

          // Limit to reasonable number of rows for real-time refresh
          const MAX_REFRESH_ROWS = 100;
          const response = await fetch(`${API_BASE_URL}/bd-details/range/?start_date=${start}&end_date=${end}&offset=0&limit=${MAX_REFRESH_ROWS}`, { headers });

          if (response.ok) {
            const payload = await response.json();
            const bdData = Array.isArray(payload) ? payload : (payload?.data || []);

            // Use all data without date filtering
            const filteredBdData = bdData;

            filteredBdData.forEach((row: any) => {
              const entryDate = normalizeDateForSort(row.entry_date);
              const rowId = row.id || row.pk;
              if (entryDate) {
                const compositeKey = rowId ? `${entryDate}_${rowId}` : entryDate;
                loadedData[compositeKey] = {
                  id: rowId,
                  date: entryDate,
                  'Budget PR for the month': row.budget_pr_for_month || getCurrentMonthPR(),
                  'Equipment': row.equipment || '',
                  'BD Category': row.bd_category || '',
                  'ICR No': row.icr_no || '',
                  'Inverter No.': row.inverter_no || '',
                  'BD From': row.bd_from || '',
                  'BD up  to': row.bd_up_to || '',
                  'BD Minutes': row.bd_minutes !== null && row.bd_minutes !== undefined ? String(row.bd_minutes) : '',
                  'BD Hours': row.bd_hours !== null && row.bd_hours !== undefined ? String(row.bd_hours) : '',
                  'PA BD Hours': row.pa_bd_hours !== null && row.pa_bd_hours !== undefined ? String(row.pa_bd_hours) : '',
                  'A = DC Capacity of affected equipment (kW)': row.dc_capacity_kw !== null && row.dc_capacity_kw !== undefined ? String(row.dc_capacity_kw) : '',
                  'B = POA lost during BD (kWh/m2)': row.poa_lost_kwh_per_m2 !== null && row.poa_lost_kwh_per_m2 !== undefined ? String(row.poa_lost_kwh_per_m2) : '',
                  'C=A*B': row.c_a_times_b !== null && row.c_a_times_b !== undefined ? String(row.c_a_times_b) : '',
                  'Generation loss kWh': row.generation_loss_kwh !== null && row.generation_loss_kwh !== undefined ? String(row.generation_loss_kwh) : '',
                  "SCB's /QS/UNIT": row.scb_qs_unit || '',
                  'BD Description': row.bd_description || '',
                  'Corrective Action Taken': row.corrective_action_taken || '',
                  'Current Status': row.current_status || '',
                  'Effective loss of POA': row.effective_loss_of_poa || '',
                  'Scope': row.scope || '',
                  'Which Equipment is in off condition': row.equipment_off_condition || '',
                  'Cause (Error Code)': row.cause_error_code || ''
                };
                if (!loadedKeys.includes(compositeKey)) {
                  loadedKeys.push(compositeKey);
                }
              }
            });
          }
        }

        loadedKeys.sort((a, b) => {
          const dateA = a.includes('_') ? a.split('_')[0] : a;
          const dateB = b.includes('_') ? b.split('_')[0] : b;
          if (dateA !== dateB) {
            return dateA.localeCompare(dateB);
          }
          const idA = a.includes('_') ? parseInt(a.split('_')[1] || '0') : 0;
          const idB = b.includes('_') ? parseInt(b.split('_')[1] || '0') : 0;
          return idB - idA;
        });

        if (Object.keys(loadedData).length > 0) {
          const { data: filteredData, rowKeys: filteredKeys } = filterRemovedRows(
            loadedData,
            loadedKeys,
            'BD Details',
            siteCode,
            userIdForLoad
          );

          // Merge fresh data with current data to preserve unsaved rows
          // Rows that exist in current data but not in filteredData are unsaved and should be preserved
          const mergedData = { ...data }; // Start with current data (includes unsaved rows)
          const mergedKeys: string[] = []; // Will build merged keys list

          // Track which keys are from database
          const dbKeysSet = new Set(filteredKeys);

          // First, add all rows from database (these are saved)
          filteredKeys.forEach(key => {
            if (filteredData[key]) {
              mergedData[key] = filteredData[key];
              mergedKeys.push(key);
            }
          });

          // Then, preserve all rows that exist in current data but not in database (unsaved rows)
          // Also preserve rows that start with the date in justAddedRowRef (for newly added rows)
          const extractDateFromKey = (key: string): string => {
            return key.includes('_') ? key.split('_')[0] : key;
          };

          rowKeys.forEach(key => {
            const keyDate = extractDateFromKey(key);
            const isNewlyAddedRow = justAddedRowRef.current && keyDate === justAddedRowRef.current;

            if ((!dbKeysSet.has(key) || isNewlyAddedRow) && data[key]) {
              // This is an unsaved row (exists locally but not in DB), preserve it
              mergedData[key] = data[key];
              if (!mergedKeys.includes(key)) {
                mergedKeys.push(key);
              }
              BD_DEBUG && console.log(`?? [REAL-TIME] Preserving unsaved BD Details row: ${key}${isNewlyAddedRow ? ' (newly added)' : ''}`);
            }
          });

          // Sort merged keys (by date, then by ID for same dates)
          mergedKeys.sort((a, b) => {
            const dateA = a.includes('_') ? a.split('_')[0] : a;
            const dateB = b.includes('_') ? b.split('_')[0] : b;
            if (dateA !== dateB) {
              return dateA.localeCompare(dateB);
            }
            const idA = a.includes('_') ? parseInt(a.split('_')[1] || '0') : 0;
            const idB = b.includes('_') ? parseInt(b.split('_')[1] || '0') : 0;
            return idB - idA;
          });

          const currentDataSignature = JSON.stringify(Object.keys(data).sort());
          const newDataSignature = JSON.stringify(Object.keys(mergedData).sort());

          // Sort merged keys (by date, then by ID for same dates) - NO CLAMPING, show ALL data
          const sortedMergedKeys = sortKeysByDateValue(mergedKeys, mergedData);

          // CRITICAL: Update cursorDateRef to ensure it's always set
          if (sortedMergedKeys.length > 0) {
            updateOldestLoadedRefs(sortedMergedKeys[0], mergedData);
          }

          if (currentDataSignature !== newDataSignature ||
            JSON.stringify(rowKeys.sort()) !== JSON.stringify(sortedMergedKeys.sort())) {
            BD_DEBUG && console.log(`?? [REAL-TIME] Refreshed BD Details: ${sortedMergedKeys.length} records (preserved ${rowKeys.length - filteredKeys.length} unsaved rows, showing all data)`);
            setAllData(mergedData, sortedMergedKeys);
          }
        }
      } catch (error) {
        console.error('Error refreshing BD Details data:', error);
      }
    };

    // TEMPORARILY DISABLED: Real-time refresh to prevent infinite loop and wrong date range
    // TODO: Re-enable after fixing date range issue
    // const intervalId = isTabVisible ? setInterval(loadFreshData, 30000) : null;
    // const timeoutId = isTabVisible ? setTimeout(loadFreshData, 5000) : null;

    // return () => {
    //   if (intervalId) clearInterval(intervalId);
    //   if (timeoutId) clearTimeout(timeoutId);
    // };
  }, [isInitialLoad, site, siteCode, user, setAllData, isTabVisible]);

  /**
   * Load more older data when user scrolls up (lazy loading)
   * Memoized with useCallback to ensure it uses latest data and rowKeys
   */
  const loadMoreOlderData = useCallback(async () => {
    if (isLoadingMoreRef.current) return;
    if (!hasMoreDataRef.current) return;
    if (!cursorDateRef.current) return;

    isLoadingMoreRef.current = true;
    setIsLoadingOlder(true);

    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) return;

      const backendUser = userResponse.user;
      const resolvedUserId = resolveUserFilterId(user, backendUser);
      const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;

      BD_DEBUG && console.log(`?? [LOAD MORE] Fetching 50 older rows from backend using cursor: ${cursorDateRef.current}, ${cursorIdRef.current}`);

      const serviceResponse = await bdDetailsService.loadByDateRange({
        userId: userIdForLoad,
        siteCode: siteCode,
        siteId: user?.siteId || backendUser?.site?.site_id || null,
        limit: 50,
        cursorDate: cursorDateRef.current,
        cursorId: cursorIdRef.current ?? undefined,
      });

      if (serviceResponse.ok) {
        const bdData = serviceResponse.data || [];
        
        // Save pagination cursors for next "Load More"
        if (serviceResponse.meta) {
          cursorDateRef.current = serviceResponse.meta.next_cursor_date;
          cursorIdRef.current = serviceResponse.meta.next_cursor_id;
          hasMoreDataRef.current = serviceResponse.meta.has_more;
          setHasMoreData(serviceResponse.meta.has_more);
        } else {
          hasMoreDataRef.current = false;
          setHasMoreData(false);
        }

        if (bdData.length === 0) {
          setStatus({ type: 'warning', message: 'No more data available.' });
          setTimeout(() => setStatus({ type: null, message: '' }), 3000);
          return;
        }

        let newLoadedData: any = {};
        let newLoadedKeys: string[] = [];
        const keyUsageCount: Record<string, number> = {};

        bdData.forEach((row: any, idx: number) => {
          const mapped = mapBackendRowToFrontend(row, idx);
          if (!mapped.key || !mapped.data) return;

          let finalKey = mapped.key;
          if (newLoadedData[finalKey] || rowKeysRef.current.includes(finalKey)) {
            keyUsageCount[mapped.key] = (keyUsageCount[mapped.key] || 0) + 1;
            finalKey = `${mapped.key}__dup${keyUsageCount[mapped.key]}`;
          }

          newLoadedData[finalKey] = mapped.data;
          newLoadedKeys.push(finalKey);
        });

        // Merge older data at the TOP (beginning) of the array
        const mergedKeys = [...newLoadedKeys, ...rowKeysRef.current];
        const mergedData = { ...newLoadedData, ...dataRef.current };

        // Save scroll state BEFORE setting data so we don't jump
        if (containerRef.current) {
          scrollRestoreRef.current = {
            scrollTop: containerRef.current.scrollTop,
            scrollHeight: containerRef.current.scrollHeight,
          };
        }

        setAllData(mergedData, mergedKeys);
      } else {
        setStatus({ type: 'error', message: serviceResponse.error || 'Failed to load older data' });
      }
    } catch (error: any) {
      console.error('? [LOAD MORE] Error:', error.message);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingOlder(false);
    }
  }, [siteCode, user, setAllData]);

  /**
   * Auto-sort rows by date (ascending), then by ID for same dates
   * Handles composite keys (date_id format)
   * 
   * This effect runs based on hookRowKeys to avoid sync conflicts
   */
  useEffect(() => {
    if (hookRowKeys.length === 0) {
      return;
    }

    // Wait a bit after initial load to ensure data is ready
    if (isInitialLoad) {
      return;
    }

    // Skip if we're in the middle of a manual sort operation
    if (isManuallySortingRef.current) {
      return;
    }

    const today = new Date().toISOString().split('T')[0];

    // Separate today's keys from historical keys (handle composite keys)
    const todayKeys = hookRowKeys.filter(key => {
      const dateFromKey = getDateForKey(key, data);
      return dateFromKey === today;
    });

    const historicalKeys = hookRowKeys.filter(key => {
      const dateFromKey = getDateForKey(key, data);
      return dateFromKey !== today;
    });

    // Sort historical by date, then by ID (for same dates) - create new array
    const sortedHistorical = sortKeysByDateValue(historicalKeys, data);

    // Sort all keys by date, then by ID (for same dates)
    const expectedKeys = sortKeysByDateValue([...hookRowKeys], data);

    const updatedData = { ...data };

    // Compare stringified arrays to avoid reference comparison issues
    const isAlreadySorted = ordersMatch(expectedKeys, hookRowKeys);

    if (!isAlreadySorted) {
      BD_DEBUG && console.log('?? AUTO-SORTING ROWS (from hookRowKeys):', {
        current: hookRowKeys.map(k => k.includes('_') ? k.split('_')[0] : k),
        expected: expectedKeys.map(k => k.includes('_') ? k.split('_')[0] : k),
        todayKeys: todayKeys.length,
        historicalKeys: historicalKeys.length
      });

      // Sort using setTimeout to avoid calling flushSync during render
      isManuallySortingRef.current = true;
      setTimeout(() => {
        setRowKeysLocal(expectedKeys);
        setTableKey(prev => prev + 1);
        setAllData(updatedData, expectedKeys);
        setTimeout(() => {
          isManuallySortingRef.current = false;
          BD_DEBUG && console.log('? Sort completed, flag reset');
        }, 150);
      }, 0);
    }
  }, [hookRowKeys.join(','), data, setAllData, isInitialLoad]);

  // Show table once initial load completes (scroll-to-bottom is handled by UniversalTable)
  useEffect(() => {
    if (!isInitialLoad && rowKeys.length > 0) {
      // Small delay to let UniversalTable position scroll first
      const timer = setTimeout(() => setIsTableVisible(true), 50);
      return () => clearTimeout(timer);
    } else if (!isInitialLoad && rowKeys.length === 0) {
      setIsTableVisible(true);
    } else {
      setIsTableVisible(false);
    }
  }, [isInitialLoad, rowKeys.length]);

  // Restore scroll position synchronously after React commits prepended rows to DOM.
  // useLayoutEffect fires after DOM mutations but BEFORE browser paint � no visible jump.
  useLayoutEffect(() => {
    if (!scrollRestoreRef.current || !containerRef.current || isInitialLoad) return;
    const { scrollTop, scrollHeight } = scrollRestoreRef.current;
    scrollRestoreRef.current = null;
    const diff = containerRef.current.scrollHeight - scrollHeight;
    if (diff > 0) {
      containerRef.current.style.scrollBehavior = 'auto';
      const maxScroll = containerRef.current.scrollHeight - containerRef.current.clientHeight;
      containerRef.current.scrollTop = Math.min(scrollTop + diff, maxScroll);
    }
  }, [rowKeys.length, isInitialLoad]);

  // Fallback useEffect for cases where useLayoutEffect runs before rows are fully rendered
  useEffect(() => {
    // Wait for data to be available and initial load to complete
    if (isInitialLoad || rowKeys.length === 0 || !containerRef.current) {
      return;
    }

    // If we've already set the position, don't do it again
    if (hasScrolledToBottomRef.current) return;

    const container = containerRef.current;
    const setPositionToBottom = () => {
      if (!containerRef.current || hasScrolledToBottomRef.current) return;

      const container = containerRef.current;

      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const maxScroll = scrollHeight - clientHeight;

      const hasScrollableContent = maxScroll > 0 && scrollHeight > clientHeight;

      // Disable smooth scrolling to prevent animation (set permanently)
      container.style.scrollBehavior = 'auto';

      // Set scroll position directly to bottom immediately (no animation)
      // Use maxScroll if scrollable, otherwise 0 (which shows bottom for small content)
      const targetScroll = hasScrollableContent ? maxScroll : 0;
      container.scrollTop = targetScroll;

      // Force immediate update by reading scrollTop
      const currentScroll = container.scrollTop;

      // Verify position was set correctly (allow some tolerance)
      if (Math.abs(currentScroll - targetScroll) < 1 || currentScroll >= targetScroll - 5 || (!hasScrollableContent && currentScroll === 0)) {
        hasScrolledToBottomRef.current = true;

        // Show table only after scroll is positioned correctly
        setIsTableVisible(true);

        BD_DEBUG && console.log('?? [BD Details] useEffect fallback: Set position to bottom (sheet starts from bottom rows)', {
          scrollHeight,
          clientHeight,
          maxScroll,
          targetScroll,
          actualScrollTop: container.scrollTop,
          hasScrollableContent,
          rowCount: rowKeys.length
        });
      }
    };

    // Use MutationObserver to watch for DOM changes and set position when table updates
    const observer = new MutationObserver(() => {
      if (!hasScrolledToBottomRef.current && containerRef.current) {
        setPositionToBottom();
      }
    });

    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      });
    }

    // Try immediately
    setPositionToBottom();

    // Try with requestAnimationFrame
    requestAnimationFrame(() => {
      setPositionToBottom();
      requestAnimationFrame(() => {
        setPositionToBottom();
      });
    });

    // Try after various delays to catch different render stages
    const timeouts = [
      setTimeout(setPositionToBottom, 0),
      setTimeout(setPositionToBottom, 50),
      setTimeout(setPositionToBottom, 100),
      setTimeout(setPositionToBottom, 200),
      setTimeout(setPositionToBottom, 300),
      setTimeout(setPositionToBottom, 500),
      setTimeout(setPositionToBottom, 800)
    ];

    return () => {
      observer.disconnect();
      timeouts.forEach(clearTimeout);
    };
  }, [isInitialLoad, rowKeys.length, tableKey]);

  /**
   * Add new row with auto-incremented date
   * Also checks database for existing data for this date and loads it if found
   */
  const handleAddRow = async () => {
    let newDate: string;

    if (rowKeys.length > 0) {
      // Get latest date and add 1 day
      // Extract dates from composite keys (format: "date_id" or just "date")
      const extractDate = (key: string): string => {
        // If key contains underscore, extract the date part (before underscore)
        if (key.includes('_')) {
          return key.split('_')[0];
        }
        return key;
      };

      const sortedDates = [...rowKeys]
        .map(key => extractDate(key))
        .filter(date => {
          // Validate date format (YYYY-MM-DD)
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          return dateRegex.test(date);
        })
        .sort();

      if (sortedDates.length > 0) {
        const latestDate = sortedDates[sortedDates.length - 1];
        const nextDay = new Date(latestDate);

        // Validate the date is valid
        if (isNaN(nextDay.getTime())) {
          // Invalid date, use today instead
          newDate = new Date().toISOString().split('T')[0];
        } else {
          nextDay.setDate(nextDay.getDate() + 1);
          newDate = nextDay.toISOString().split('T')[0];
        }
      } else {
        // No valid dates found, use today
        newDate = new Date().toISOString().split('T')[0];
      }
    } else {
      newDate = new Date().toISOString().split('T')[0];
    }

    BD_DEBUG && console.log(`? Adding new row: ${newDate}`);

    // Mark this row as just added to prevent real-time refresh from overwriting it
    justAddedRowRef.current = newDate;

    // Check if data exists in database for this date
    try {
      const userResponse = await backendApi.getCurrentUser();
      if (userResponse.success && userResponse.user?.id) {
        const backendUser = userResponse.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
        const isAdmin = shouldLoadAllRecords(user, backendUser);

        // Load data for this specific date
        let existingDataForDate: any[] = [];

        if (isAdmin) {
          // Admin: Use get-dgr endpoint for the specific date
          try {
            const API_BASE_URL = 'http://localhost:8000/api/v1';
            const token = sessionStorage.getItem('access_token');
            const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=BD`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const payload = await response.json();
              const bdData = payload?.data?.bd || [];
              // Filter to only this date
              existingDataForDate = bdData.filter((row: any) => row.entry_date === newDate);
            }
          } catch (error) {
            BD_DEBUG && console.warn('Error loading data for date via get-dgr:', error);
          }
        } else {
          // Regular user: Load their data and filter by date
          const loadResult = await bdDetailsService.loadByUser(userIdForLoad);
          if (loadResult.ok && loadResult.data) {
            existingDataForDate = loadResult.data.filter((row: any) => row.entry_date === newDate);
          }
        }

        // If data exists, populate rows with that data
        if (existingDataForDate.length > 0) {
          BD_DEBUG && console.log(`?? Found ${existingDataForDate.length} existing record(s) for date ${newDate}, loading data...`);

          const updatedData = { ...data };
          const newKeys: string[] = [];

          // Create rows for each existing record
          existingDataForDate.forEach((row: any) => {
            const rowId = row.id || row.pk;
            const baseKey = rowId ? `${newDate}_${rowId}` : `${newDate}_temp${Date.now()}`;
            const compositeKey = makeUniqueRowKey(baseKey, updatedData, [...rowKeys, ...newKeys]);

            updatedData[compositeKey] = {
              id: rowId,
              date: newDate,
              'Budget PR for the month': row.budget_pr_for_month || getCurrentMonthPR(),
              'Equipment': row.equipment || '',
              'BD Category': row.bd_category || '',
              'ICR No': row.icr_no || '',
              'Inverter No.': row.inverter_no || '',
              'BD From': row.bd_from || '',
              'BD up  to': row.bd_up_to || '',
              'BD Minutes': row.bd_minutes !== null && row.bd_minutes !== undefined ? String(row.bd_minutes) : '',
              'BD Hours': row.bd_hours !== null && row.bd_hours !== undefined ? String(row.bd_hours) : '',
              'PA BD Hours': row.pa_bd_hours !== null && row.pa_bd_hours !== undefined ? String(row.pa_bd_hours) : '',
              'A = DC Capacity of affected equipment (kW)': row.dc_capacity_kw !== null && row.dc_capacity_kw !== undefined ? String(row.dc_capacity_kw) : '',
              'B = POA lost during BD (kWh/m2)': row.poa_lost_kwh_per_m2 !== null && row.poa_lost_kwh_per_m2 !== undefined ? String(row.poa_lost_kwh_per_m2) : '',
              'C=A*B': row.c_a_times_b !== null && row.c_a_times_b !== undefined ? String(row.c_a_times_b) : '',
              'Generation loss kWh': row.generation_loss_kwh !== null && row.generation_loss_kwh !== undefined ? String(row.generation_loss_kwh) : '',
              "SCB's /QS/UNIT": row.scb_qs_unit || '',
              'BD Description': row.bd_description || '',
              'Corrective Action Taken': row.corrective_action_taken || '',
              'Current Status': row.current_status || '',
              'Effective loss of POA': row.effective_loss_of_poa || '',
              'Scope': row.scope || '',
              'Which Equipment is in off condition': row.equipment_off_condition || '',
              'Cause (Error Code)': row.cause_error_code || ''
            };

            newKeys.push(compositeKey);
          });

          // Add new keys to existing rowKeys and sort
          const updatedKeys = [...rowKeys, ...newKeys].sort((a, b) => {
            const dateA = a.includes('_') ? a.split('_')[0] : a;
            const dateB = b.includes('_') ? b.split('_')[0] : b;
            if (dateA !== dateB) {
              return dateA.localeCompare(dateB);
            }
            // If same date, sort by ID (second part after underscore)
            const idA = a.includes('_') ? parseInt(a.split('_')[1] || '0') : 0;
            const idB = b.includes('_') ? parseInt(b.split('_')[1] || '0') : 0;
            return idB - idA; // Descending order (newer IDs first)
          });

          // Update local state
          setRowKeysLocal(updatedKeys);
          setAllData(updatedData, updatedKeys);

          setStatus({
            type: 'success',
            message: `? Loaded ${existingDataForDate.length} existing record(s) for ${newDate}`
          });

          // Focus on first new row
          setTimeout(() => {
            const firstNewIndex = updatedKeys.indexOf(newKeys[0]);
            if (firstNewIndex >= 0) {
              setFocusedCell({ rowIndex: firstNewIndex, colIndex: 1 });
              scrollToCell(firstNewIndex, 1);
            }
          }, 150);

          // Clear the flag after a delay
          setTimeout(() => {
            justAddedRowRef.current = null;
          }, 10000);

          return;
        }
      }
    } catch (error) {
      BD_DEBUG && console.warn('Error checking database for existing data:', error);
      // Continue with creating empty row if check fails
    }

    // No data found in database, create empty row
    BD_DEBUG && console.log(`?? No existing data found for ${newDate}, creating empty row`);

    const baseKey = `${newDate}_temp${Date.now()}`;
    const uniqueNewKey = makeUniqueRowKey(baseKey, data, rowKeys);

    // Create new row with date and Budget PR populated
    const updatedData = {
      ...data,
      [uniqueNewKey]: createEmptyRow(newDate)
    };

    // Add new date and sort all by date, then by ID
    const updatedKeys = [...rowKeys, uniqueNewKey].sort((a, b) => {
      const dateA = a.includes('_') ? a.split('_')[0] : a;
      const dateB = b.includes('_') ? b.split('_')[0] : b;
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }
      // If same date, sort by ID (second part after underscore)
      const idA = a.includes('_') ? parseInt(a.split('_')[1] || '0') : 0;
      const idB = b.includes('_') ? parseInt(b.split('_')[1] || '0') : 0;
      return idB - idA; // Descending order (newer IDs first)
    });

    // Update local state immediately
    setRowKeysLocal(updatedKeys);
    setAllData(updatedData, updatedKeys);

    // Clear the flag after a delay to allow real-time refresh to resume
    setTimeout(() => {
      justAddedRowRef.current = null;
    }, 10000); // 10 seconds should be enough for user to start editing

    // Focus on new row
    setTimeout(() => {
      const newIndex = updatedKeys.indexOf(uniqueNewKey);
      setFocusedCell({ rowIndex: newIndex, colIndex: 1 });
      scrollToCell(newIndex, 1);
    }, 150);
  };

  /**
   * Handle row key (date) change
   */
  const handleRowKeyChange = (index: number, newKey: string) => {
    const oldKey = rowKeys[index];

    // Validate date format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newKey)) {
      setStatus({
        type: 'error',
        message: '? Invalid date format. Use YYYY-MM-DD.'
      });
      return;
    }

    // Extract ID from old key if it's a composite key
    const oldRowData = data[oldKey];
    const rowId = oldRowData?.id;

    // Create new composite key and ensure uniqueness so same-date rows are never overwritten.
    const tempSuffix = oldKey.includes('_') ? oldKey.split('_').slice(1).join('_') : `temp${Date.now()}`;
    const baseCompositeKey = rowId ? `${newKey}_${rowId}` : `${newKey}_${tempSuffix}`;
    const dataWithoutOldKey = { ...data };
    delete dataWithoutOldKey[oldKey];
    const keysWithoutOldKey = rowKeys.filter(k => k !== oldKey);
    const newCompositeKey = makeUniqueRowKey(baseCompositeKey, dataWithoutOldKey, keysWithoutOldKey);

    // Check if new composite key already exists
    if (keysWithoutOldKey.includes(newCompositeKey)) {
      setStatus({
        type: 'error',
        message: `? Date ${newKey} already exists.`
      });
      return;
    }

    const today = new Date().toISOString().split('T')[0];

    // Update date in row data and create new key
    const updatedData = { ...data };
    // If data already exists for newCompositeKey (e.g., loaded from database), preserve it
    // Otherwise, copy from oldKey and update the date field
    if (updatedData[newCompositeKey]) {
      // Data already exists for new composite key (likely loaded from database) - just ensure date is correct
      updatedData[newCompositeKey] = { ...updatedData[newCompositeKey], date: newKey };
    } else {
      // No data for new composite key - copy from old key
      updatedData[newCompositeKey] = { ...updatedData[oldKey], date: newKey };
    }
    delete updatedData[oldKey];

    // Replace the old key with new composite key in the keys array and sort
    const keysAfterChange = rowKeys.map(k => k === oldKey ? newCompositeKey : k);

    // Sort all keys by date, then by ID (for same dates)
    const updatedKeys = [...keysAfterChange].sort((a, b) => {
      const dateA = a.includes('_') ? a.split('_')[0] : a;
      const dateB = b.includes('_') ? b.split('_')[0] : b;
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }
      // If same date, sort by ID (second part after underscore)
      const idA = a.includes('_') ? parseInt(a.split('_')[1] || '0') : 0;
      const idB = b.includes('_') ? parseInt(b.split('_')[1] || '0') : 0;
      return idB - idA; // Descending order (newer IDs first)
    });

    BD_DEBUG && console.log('?? Reordering rows after date change:', {
      oldKey,
      newKey,
      oldIndex: index,
      beforeSort: keysAfterChange,
      afterSort: updatedKeys,
      newIndex: updatedKeys.indexOf(newCompositeKey)
    });

    isManuallySortingRef.current = true;

    // Create completely new references to ensure React detects the change
    const finalKeys = Array.from(updatedKeys); // Use Array.from to create new reference
    const finalData = { ...updatedData }; // Create new object reference

    // Update state using setTimeout to avoid flushSync during render
    setTimeout(() => {
      setRowKeysLocal(finalKeys);
      setTableKey(prev => prev + 1);
      setAllData(finalData, finalKeys);
    }, 0);

    // Update focused cell to the new position after reordering
    const newIndex = updatedKeys.indexOf(newCompositeKey);
    if (newIndex >= 0 && newIndex !== index) {
      setTimeout(() => {
        setFocusedCell({ rowIndex: newIndex, colIndex: focusedCell.colIndex });
        scrollToCell(newIndex, focusedCell.colIndex);
      }, 50);
    }

    // Reset flag after state update completes
    setTimeout(() => {
      isManuallySortingRef.current = false;
    }, 100);

    BD_DEBUG && console.log(`? Date changed successfully - row moved from index ${index} to ${updatedKeys.indexOf(newCompositeKey)}`);
  };

  /**
   * Load existing data for a specific date from database
   */
  const loadDataForDate = async (date: string): Promise<any | null> => {
    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        return null;
      }

      const backendUser = userResponse.user;
      const resolvedUserId = resolveUserFilterId(user, backendUser);
      const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
      const isAdmin = shouldLoadAllRecords(user, backendUser);

      let existingDataForDate: any[] = [];

      if (isAdmin) {
        // Admin: Use get-dgr endpoint
        try {
          const API_BASE_URL = 'http://localhost:8000/api/v1';
          const token = sessionStorage.getItem('access_token');
          const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=BD`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const payload = await response.json();
            const bdData = payload?.data?.bd || [];
            existingDataForDate = bdData.filter((row: any) => row.entry_date === date);
          }
        } catch (error) {
          BD_DEBUG && console.warn('Error loading data for date via get-dgr:', error);
        }
      } else {
        // Regular user: Load their data and filter by date
        const loadResult = await bdDetailsService.loadByUser(userIdForLoad);
        if (loadResult.ok && loadResult.data) {
          existingDataForDate = loadResult.data.filter((row: any) => row.entry_date === date);
        }
      }

      // Return first matching record if exists
      return existingDataForDate.length > 0 ? existingDataForDate[0] : null;
    } catch (error) {
      console.error('Error loading data for date:', error);
      return null;
    }
  };

  /**
   * Wrapper for updateCell that intercepts date column changes
   * When date column is changed, check database for existing data and load it if found
   */
  const handleCellChange = async (rowKey: string, columnKey: string, value: any) => {
    // If this is a date column change, check database and load data if exists
    if (columnKey === 'date' && value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const rowIndex = rowKeys.indexOf(rowKey);
      if (rowIndex >= 0) {
        // First update the cell value
        updateCell(rowKey, columnKey, value);

        // Check if data exists in database for this date
        const existingData = await loadDataForDate(value);

        if (existingData) {
          // Data exists - populate the row with existing data
          BD_DEBUG && console.log(`?? Found existing data for date ${value}, loading...`);

          const rowData = data[rowKey];
          const rowId = rowData?.id;

          // Map backend data to frontend format (using same mapping as initial load)
          const mappedRow: any = {
            id: existingData.id || existingData.pk || rowId,
            date: value,
            'Budget PR for the month': existingData.budget_pr_for_month || getCurrentMonthPR(),
            'Equipment': existingData.equipment || '',
            'BD Category': existingData.bd_category || '',
            'ICR No': existingData.icr_no || '',
            'Inverter No.': existingData.inverter_no || '',
            'BD From': existingData.bd_from || '',
            'BD up  to': existingData.bd_up_to || '',
            'BD Minutes': existingData.bd_minutes !== null && existingData.bd_minutes !== undefined ? String(existingData.bd_minutes) : '',
            'BD Hours': existingData.bd_hours !== null && existingData.bd_hours !== undefined ? String(existingData.bd_hours) : '',
            'PA BD Hours': existingData.pa_bd_hours !== null && existingData.pa_bd_hours !== undefined ? String(existingData.pa_bd_hours) : '',
            'A = DC Capacity of affected equipment (kW)': existingData.dc_capacity_kw !== null && existingData.dc_capacity_kw !== undefined ? String(existingData.dc_capacity_kw) : '',
            'B = POA lost during BD (kWh/m2)': existingData.poa_lost_kwh_per_m2 !== null && existingData.poa_lost_kwh_per_m2 !== undefined ? String(existingData.poa_lost_kwh_per_m2) : '',
            'C=A*B': existingData.c_a_times_b !== null && existingData.c_a_times_b !== undefined ? String(existingData.c_a_times_b) : '',
            'Generation loss kWh': existingData.generation_loss_kwh !== null && existingData.generation_loss_kwh !== undefined ? String(existingData.generation_loss_kwh) : '',
            "SCB's /QS/UNIT": existingData.scb_qs_unit || '',
            'BD Description': existingData.bd_description || '',
            'Corrective Action Taken': existingData.corrective_action_taken || '',
            'Current Status': existingData.current_status || '',
            'Effective loss of POA': existingData.effective_loss_of_poa || '',
            'Scope': existingData.scope || '',
            'Which Equipment is in off condition': existingData.equipment_off_condition || '',
            'Cause (Error Code)': existingData.cause_error_code || ''
          };

          // Update the row data with loaded data
          const updatedData = { ...data };
          updatedData[rowKey] = mappedRow;
          setAllData(updatedData, rowKeys);

          setStatus({
            type: 'success',
            message: `? Loaded existing data for ${value}`
          });
        } else {
          // No data exists - keep as empty row
          BD_DEBUG && console.log(`?? No existing data for date ${value}, keeping as empty row`);
        }

        // Then update the row key (which will trigger sorting)
        // Use a small delay to ensure data loading completes first
        setTimeout(() => {
          handleRowKeyChange(rowIndex, value);
        }, 100);
        return;
      }
    }

    // For all other columns, use normal updateCell
    updateCell(rowKey, columnKey, value);
  };

  /**
   * Manual sort function - forces rows to be sorted by date
   * Collects date values directly from frontend date inputs and sorts based on those values
   * 
   * IMPORTANT: This is FRONTEND-ONLY sorting. It only affects the display order.
   * - Does NOT call any backend APIs
   * - Does NOT modify backend data
   * - Does NOT affect save/load operations (data is saved/loaded based on row content, not order)
   * - Only reorders the visual display of rows in the table
   */
  const handleManualSort = () => {
    BD_DEBUG && console.log('?? Manual sort triggered - collecting dates from frontend inputs');

    // Get the latest state values to ensure we have current data
    const currentRowKeys = [...rowKeys];
    const currentData = { ...data };

    if (currentRowKeys.length === 0) {
      setStatus({ type: 'warning', message: 'No rows to sort' });
      return;
    }

    // Create an array of rows with their actual date values from frontend inputs
    // Priority: 1) date field in row data (from calendar input), 2) date from row key
    const rowsWithDates = currentRowKeys.map(key => {
      const rowData = currentData[key];

      // Get the normalized date value (uses row data first, then falls back to key)
      const dateValue = getDateForKey(key, currentData);

      // Extract ID from composite key if present
      const rowId = key.includes('_') ? parseInt(key.split('_')[1] || '0') : 0;
      const actualRowId = rowData?.id || rowId;

      return {
        key,
        date: dateValue,
        id: actualRowId,
        data: rowData
      };
    });

    BD_DEBUG && console.log('?? Collected dates from frontend:', rowsWithDates.map(r => ({
      key: r.key,
      date: r.date,
      id: r.id
    })));

    // Sort rows by date (ascending), then by ID (descending for same dates)
    rowsWithDates.sort((a, b) => {
      const dateA = normalizeDateForSort(a.date);
      const dateB = normalizeDateForSort(b.date);
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }

      // If same date, sort by ID (descending order - newer entries first)
      return b.id - a.id;
    });

    // Extract sorted keys
    const expectedKeys = rowsWithDates.map(row => row.key);

    // Check if already sorted
    const isAlreadySorted = JSON.stringify(expectedKeys) === JSON.stringify(currentRowKeys);
    if (isAlreadySorted) {
      setStatus({ type: 'info', message: 'Rows are already sorted by date' });
      return;
    }

    // Create new data object with sorted keys (preserve all data)
    const sortedData: any = {};
    expectedKeys.forEach(key => {
      if (currentData[key]) {
        sortedData[key] = currentData[key];
      }
    });

    BD_DEBUG && console.log('?? Manual sort - reordering:', {
      before: currentRowKeys.map(k => {
        const row = rowsWithDates.find(r => r.key === k);
        return row ? row.date : (k.includes('_') ? k.split('_')[0] : k);
      }),
      after: expectedKeys.map(k => {
        const row = rowsWithDates.find(r => r.key === k);
        return row ? row.date : (k.includes('_') ? k.split('_')[0] : k);
      }),
      count: expectedKeys.length
    });

    // Set flag to prevent auto-sort from interfering
    isManuallySortingRef.current = true;

    // Update state in sequence to ensure proper re-render
    setTimeout(() => {
      // First update the data and rowKeys together
      setAllData(sortedData, expectedKeys);

      // Then update local state
      setRowKeysLocal(expectedKeys);

      // Force table re-render by incrementing tableKey
      setTableKey(prev => prev + 1);

      setTimeout(() => {
        isManuallySortingRef.current = false;
        setStatus({ type: 'success', message: `? Sorted ${expectedKeys.length} row(s) by date!` });
        BD_DEBUG && console.log('? Sort completed - rows reordered by frontend date values:',
          expectedKeys.map(k => {
            const row = rowsWithDates.find(r => r.key === k);
            return row ? row.date : (k.includes('_') ? k.split('_')[0] : k);
          }).slice(0, 10)
        );
      }, 150);
    }, 0);
  };

  /**
   * Save all BD Details to backend
   */
  const save = async () => {
    // Prevent double-submission
    if (isSaving || isExporting) {
      BD_DEBUG && console.log('?? Save already in progress, ignoring duplicate call');
      return;
    }

    setIsSaving(true);

    try {
      const entriesToSave: Array<{ entry: BDDetailsDataRow; key: string }> = [];

      BD_DEBUG && console.log(`?? Preparing to save ${rowKeys.length} rows...`);

      rowKeys.forEach(key => {
        const rowData = data[key];

        // Extract actual date from composite key or use rowData.date
        const actualDate = rowData.date || (key.includes('_') ? key.split('_')[0] : key);
        const rowId = rowData.id; // Get ID if row was loaded from database

        // Helper function to parse time string (handles both 24-hour and 12-hour with AM/PM)
        const parseTimeToMinutes = (timeStr: string): number | null => {
          if (!timeStr || typeof timeStr !== 'string' || timeStr.trim() === '') return null;
          try {
            const cleanTime = timeStr.trim().toUpperCase();
            const hasAMPM = cleanTime.includes('AM') || cleanTime.includes('PM');

            if (hasAMPM) {
              const isPM = cleanTime.includes('PM');
              const timePart = cleanTime.replace(/AM|PM/gi, '').trim();
              const [hours, minutes = '0'] = timePart.split(':');
              let h = parseInt(hours, 10);
              const m = parseInt(minutes, 10);

              if (isNaN(h) || isNaN(m)) return null;

              if (isPM && h !== 12) h += 12;
              if (!isPM && h === 12) h = 0;

              return h * 60 + m;
            } else {
              const [hours, minutes = '0'] = cleanTime.split(':');
              const h = parseInt(hours, 10);
              const m = parseInt(minutes, 10);

              if (isNaN(h) || isNaN(m)) return null;

              return h * 60 + m;
            }
          } catch {
            return null;
          }
        };

        // Calculate BD Minutes from BD From and BD up to
        let bdMinutes: number | null = null;
        if (rowData['BD Minutes'] && rowData['BD Minutes'].toString().trim() !== '') {
          const parsed = parseInt(rowData['BD Minutes'].toString().trim());
          bdMinutes = isNaN(parsed) ? null : parsed;
        } else if (rowData['BD From'] && rowData['BD up  to']) {
          const fromMinutes = parseTimeToMinutes(rowData['BD From']);
          const toMinutes = parseTimeToMinutes(rowData['BD up  to']);
          if (fromMinutes !== null && toMinutes !== null) {
            bdMinutes = ((toMinutes - fromMinutes + 1440) % 1440);
            if (bdMinutes === 0 && fromMinutes !== toMinutes) {
              // Handle next day scenario
              bdMinutes = (1440 - fromMinutes) + toMinutes;
            }
          }
        }

        // Calculate BD Hours from BD Minutes
        let bdHours: number | null = null;
        if (rowData['BD Hours'] && rowData['BD Hours'].toString().trim() !== '') {
          const parsed = parseFloat(rowData['BD Hours'].toString().trim());
          bdHours = isNaN(parsed) ? null : parsed;
        } else if (bdMinutes !== null) {
          bdHours = parseFloat((bdMinutes / 60).toFixed(2));
        }

        // PA BD Hours = BD Hours
        const paBdHours = bdHours;

        // Calculate C=A*B
        let cATimesB: number | null = null;
        const aValue = rowData['A = DC Capacity of affected equipment (kW)'] ? parseFloat(rowData['A = DC Capacity of affected equipment (kW)']) : null;
        const bValue = rowData['B = POA lost during BD (kWh/m2)'] ? parseFloat(rowData['B = POA lost during BD (kWh/m2)']) : null;

        if (rowData['C=A*B'] && rowData['C=A*B'].toString().trim() !== '') {
          const parsed = parseFloat(rowData['C=A*B'].toString().trim());
          cATimesB = isNaN(parsed) ? null : parsed;
        } else if (aValue !== null && bValue !== null && !isNaN(aValue) && !isNaN(bValue)) {
          cATimesB = parseFloat((aValue * bValue).toFixed(2));
        }

        // Calculate Generation Loss kWh = C * PR / 100
        let generationLoss: number | null = null;
        const prString = rowData['Budget PR for the month'] || '';
        const pr = prString ? parseFloat(prString.toString().replace('%', '').trim()) : null;

        if (rowData['Generation loss kWh'] && rowData['Generation loss kWh'].toString().trim() !== '') {
          const parsed = parseFloat(rowData['Generation loss kWh'].toString().trim());
          generationLoss = isNaN(parsed) ? null : parsed;
        } else if (cATimesB !== null && pr !== null && !isNaN(pr)) {
          // Allow 0 values - 0 is a valid result when PR is 0%
          generationLoss = parseFloat((cATimesB * pr / 100).toFixed(2));
          if (generationLoss === 0 && pr === 0) {
            generationLoss = 0; // Explicitly keep 0 when PR is 0%
          }
        }

        BD_DEBUG && console.log(`?? Calculated values for ${key} (date: ${actualDate}, id: ${rowId || 'new'}):`, {
          bdMinutes,
          bdHours,
          paBdHours,
          cATimesB,
          generationLoss
        });

        // Helper function to convert empty strings to null
        const cleanValue = (value: any): any => {
          if (value === null || value === undefined) return null;
          if (typeof value === 'string' && value.trim() === '') return null;
          return value;
        };

        // Map UI column keys to backend field names
        const entry: BDDetailsDataRow = {
          id: rowId, // Include ID if exists (for updates)
          entry_date: actualDate, // Use actual date from rowData
          site_code: siteCode, // Add site_code for backend
          budget_pr_for_month: cleanValue(rowData['Budget PR for the month']),
          equipment: cleanValue(rowData['Equipment']),
          bd_category: cleanValue(rowData['BD Category']),
          icr_no: cleanValue(rowData['ICR No']),
          inverter_no: cleanValue(rowData['Inverter No.']),
          bd_from: cleanValue(rowData['BD From']),
          bd_up_to: cleanValue(rowData['BD up  to']),
          bd_minutes: bdMinutes,
          bd_hours: bdHours,
          pa_bd_hours: paBdHours,
          dc_capacity_kw: aValue,
          poa_lost_kwh_per_m2: bValue,
          c_a_times_b: cATimesB,
          generation_loss_kwh: generationLoss,
          scb_qs_unit: cleanValue(rowData["SCB's /QS/UNIT"]),
          bd_description: cleanValue(rowData['BD Description']),
          corrective_action_taken: cleanValue(rowData['Corrective Action Taken']),
          current_status: cleanValue(rowData['Current Status']),
          effective_loss_of_poa: cleanValue(rowData['Effective loss of POA']),
          scope: cleanValue(rowData['Scope']),
          equipment_off_condition: cleanValue(rowData['Which Equipment is in off condition']),
          cause_error_code: cleanValue(rowData['Cause (Error Code)'])
        };

        // Only save if at least one field has data (excluding date and budget PR)
        const hasData = Object.entries(entry).some(([k, val]) =>
          k !== 'entry_date' && k !== 'budget_pr_for_month' && val !== null && val !== undefined && val !== ''
        );

        // Only save new rows (rows without ID) - skip rows that are already in database
        const isNewRow = !entry.id || typeof entry.id !== 'number';

        if (hasData && isNewRow) {
          entriesToSave.push({ entry, key });
          BD_DEBUG && console.log(`? Row ${key} has data and is new, will be saved`);
        } else if (hasData && !isNewRow) {
          BD_DEBUG && console.log(`? Row ${key} already exists in database (ID: ${entry.id}), skipping save`);
        } else {
          BD_DEBUG && console.log(`? Row ${key} is empty, skipping`);
        }
      });

      if (entriesToSave.length === 0) {
        setStatus({
          type: 'warning',
          message: '?? No data to save. Please enter some values.'
        });
        setIsSaving(false);
        return;
      }

      BD_DEBUG && console.log(`?? Saving ${entriesToSave.length} entries...`);

      let successCount = 0;
      let errorCount = 0;
      const savedEntries: Array<{ originalKey: string; newId?: number }> = [];

      for (const { entry, key: originalKey } of entriesToSave) {
        // Only save new entries (entries without ID) - entries with ID are already filtered out above
        // Remove ID from entry before creating (backend will assign new ID)
        const { id, ...entryWithoutId } = entry;
        const result = await bdDetailsService.save(entryWithoutId);

        if (result.ok && result.data) {
          successCount++;
          const savedId = result.data.id;
          BD_DEBUG && console.log(`? Saved: ${entry.entry_date} (New ID: ${savedId})`);

          // Track saved entries to update local state immediately
          if (savedId) {
            savedEntries.push({ originalKey, newId: savedId });
          }
        } else {
          errorCount++;
          console.error(`? Failed: ${entry.entry_date} - ${result.error}`);
        }
      }

      // Clear unsaved data from localStorage after successful save
      if (successCount > 0) {
        try {
          localStorage.removeItem(persistentStorageKey);
        } catch (error) {
          BD_DEBUG && console.warn('Failed to clear saved data:', error);
        }
      }

      if (errorCount === 0) {
        setStatus({
          type: 'success',
          message: `? Successfully saved ${successCount} record${successCount > 1 ? 's' : ''}!`
        });
      } else {
        setStatus({
          type: 'warning',
          message: `?? Saved ${successCount} record${successCount > 1 ? 's' : ''}, ${errorCount} failed.`
        });
      }

      // Reload ALL data - use get-dgr for admins (all data), loadByUser for regular users
      if (successCount > 0) {
        BD_DEBUG && console.log('?? Reloading data after save...');
        // Get current user ID
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user?.id) {
          const backendUser = userResponse.user;
          const resolvedUserId = resolveUserFilterId(user, backendUser);
          const loadUserId = (resolvedUserId ?? Number(backendUser.id)) || 0;
          const isAdmin = shouldLoadAllRecords(user, backendUser);

          let loadedData: any = {};
          let loadedKeys: string[] = [];

          if (isAdmin) {
            // Admin: Load all data for the site using get-dgr endpoint
            try {
              const API_BASE_URL = 'http://localhost:8000/api/v1';
              const token = sessionStorage.getItem('access_token');
              const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=BD`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                const payload = await response.json();
                const bdData = payload?.data?.bd || [];

                bdData.forEach((row: any) => {
                  const entryDate = row.entry_date;
                  const rowId = row.id || row.pk;

                  if (entryDate) {
                    // Use composite key: entry_date_id to handle multiple rows per date
                    const compositeKey = rowId ? `${entryDate}_${rowId}` : entryDate;

                    loadedData[compositeKey] = {
                      id: rowId, // Store ID for updates
                      date: entryDate, // Store actual date for display
                      'Budget PR for the month': row.budget_pr_for_month || getCurrentMonthPR(),
                      'Equipment': row.equipment || '',
                      'BD Category': row.bd_category || '',
                      'ICR No': row.icr_no || '',
                      'Inverter No.': row.inverter_no || '',
                      'BD From': row.bd_from || '',
                      'BD up  to': row.bd_up_to || '',
                      'BD Minutes': row.bd_minutes !== null && row.bd_minutes !== undefined ? String(row.bd_minutes) : '',
                      'BD Hours': row.bd_hours !== null && row.bd_hours !== undefined ? String(row.bd_hours) : '',
                      'PA BD Hours': row.pa_bd_hours !== null && row.pa_bd_hours !== undefined ? String(row.pa_bd_hours) : '',
                      'A = DC Capacity of affected equipment (kW)': row.dc_capacity_kw !== null && row.dc_capacity_kw !== undefined ? String(row.dc_capacity_kw) : '',
                      'B = POA lost during BD (kWh/m2)': row.poa_lost_kwh_per_m2 !== null && row.poa_lost_kwh_per_m2 !== undefined ? String(row.poa_lost_kwh_per_m2) : '',
                      'C=A*B': row.c_a_times_b !== null && row.c_a_times_b !== undefined ? String(row.c_a_times_b) : '',
                      'Generation loss kWh': row.generation_loss_kwh !== null && row.generation_loss_kwh !== undefined ? String(row.generation_loss_kwh) : '',
                      "SCB's /QS/UNIT": row.scb_qs_unit || '',
                      'BD Description': row.bd_description || '',
                      'Corrective Action Taken': row.corrective_action_taken || '',
                      'Current Status': row.current_status || '',
                      'Effective loss of POA': row.effective_loss_of_poa || '',
                      'Scope': row.scope || '',
                      'Which Equipment is in off condition': row.equipment_off_condition || '',
                      'Cause (Error Code)': row.cause_error_code || ''
                    };

                    if (!loadedKeys.includes(compositeKey)) {
                      loadedKeys.push(compositeKey);
                    }
                  }
                });
              }
            } catch (error) {
              console.error('Error loading all data via get-dgr:', error);
              // Fallback to loadByUser if get-dgr fails
              const loadResult = await bdDetailsService.loadByUser(loadUserId);
              BD_DEBUG && console.log('?? Reload result:', { ok: loadResult.ok, dataLength: loadResult.data?.length });

              if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
                loadResult.data.forEach((row: any) => {
                  const entryDate = row.entry_date;
                  const rowId = row.id || row.pk;

                  if (entryDate) {
                    // Use composite key: entry_date_id to handle multiple rows per date
                    const compositeKey = rowId ? `${entryDate}_${rowId}` : entryDate;

                    loadedData[compositeKey] = {
                      id: rowId, // Store ID for updates
                      date: entryDate, // Store actual date for display
                      'Budget PR for the month': row.budget_pr_for_month || getCurrentMonthPR(),
                      'Equipment': row.equipment || '',
                      'BD Category': row.bd_category || '',
                      'ICR No': row.icr_no || '',
                      'Inverter No.': row.inverter_no || '',
                      'BD From': row.bd_from || '',
                      'BD up  to': row.bd_up_to || '',
                      'BD Minutes': row.bd_minutes !== null && row.bd_minutes !== undefined ? String(row.bd_minutes) : '',
                      'BD Hours': row.bd_hours !== null && row.bd_hours !== undefined ? String(row.bd_hours) : '',
                      'PA BD Hours': row.pa_bd_hours !== null && row.pa_bd_hours !== undefined ? String(row.pa_bd_hours) : '',
                      'A = DC Capacity of affected equipment (kW)': row.dc_capacity_kw !== null && row.dc_capacity_kw !== undefined ? String(row.dc_capacity_kw) : '',
                      'B = POA lost during BD (kWh/m2)': row.poa_lost_kwh_per_m2 !== null && row.poa_lost_kwh_per_m2 !== undefined ? String(row.poa_lost_kwh_per_m2) : '',
                      'C=A*B': row.c_a_times_b !== null && row.c_a_times_b !== undefined ? String(row.c_a_times_b) : '',
                      'Generation loss kWh': row.generation_loss_kwh !== null && row.generation_loss_kwh !== undefined ? String(row.generation_loss_kwh) : '',
                      "SCB's /QS/UNIT": row.scb_qs_unit || '',
                      'BD Description': row.bd_description || '',
                      'Corrective Action Taken': row.corrective_action_taken || '',
                      'Current Status': row.current_status || '',
                      'Effective loss of POA': row.effective_loss_of_poa || '',
                      'Scope': row.scope || '',
                      'Which Equipment is in off condition': row.equipment_off_condition || '',
                      'Cause (Error Code)': row.cause_error_code || ''
                    };

                    if (!loadedKeys.includes(compositeKey)) {
                      loadedKeys.push(compositeKey);
                    }
                  }
                });
              }
            }
          } else {
            // Regular user: Load only their data
            const loadResult = await bdDetailsService.loadByUser(loadUserId);
            BD_DEBUG && console.log('?? Reload result:', { ok: loadResult.ok, dataLength: loadResult.data?.length });

            if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
              loadResult.data.forEach((row: any) => {
                const entryDate = row.entry_date;
                const rowId = row.id || row.pk;

                if (entryDate) {
                  // Use composite key: entry_date_id to handle multiple rows per date
                  const compositeKey = rowId ? `${entryDate}_${rowId}` : entryDate;

                  loadedData[compositeKey] = {
                    id: rowId, // Store ID for updates
                    date: entryDate, // Store actual date for display
                    'Budget PR for the month': row.budget_pr_for_month || getCurrentMonthPR(),
                    'Equipment': row.equipment || '',
                    'BD Category': row.bd_category || '',
                    'ICR No': row.icr_no || '',
                    'Inverter No.': row.inverter_no || '',
                    'BD From': row.bd_from || '',
                    'BD up  to': row.bd_up_to || '',
                    'BD Minutes': row.bd_minutes !== null && row.bd_minutes !== undefined ? String(row.bd_minutes) : '',
                    'BD Hours': row.bd_hours !== null && row.bd_hours !== undefined ? String(row.bd_hours) : '',
                    'PA BD Hours': row.pa_bd_hours !== null && row.pa_bd_hours !== undefined ? String(row.pa_bd_hours) : '',
                    'A = DC Capacity of affected equipment (kW)': row.dc_capacity_kw !== null && row.dc_capacity_kw !== undefined ? String(row.dc_capacity_kw) : '',
                    'B = POA lost during BD (kWh/m2)': row.poa_lost_kwh_per_m2 !== null && row.poa_lost_kwh_per_m2 !== undefined ? String(row.poa_lost_kwh_per_m2) : '',
                    'C=A*B': row.c_a_times_b !== null && row.c_a_times_b !== undefined ? String(row.c_a_times_b) : '',
                    'Generation loss kWh': row.generation_loss_kwh !== null && row.generation_loss_kwh !== undefined ? String(row.generation_loss_kwh) : '',
                    "SCB's /QS/UNIT": row.scb_qs_unit || '',
                    'BD Description': row.bd_description || '',
                    'Corrective Action Taken': row.corrective_action_taken || '',
                    'Current Status': row.current_status || '',
                    'Effective loss of POA': row.effective_loss_of_poa || '',
                    'Scope': row.scope || '',
                    'Which Equipment is in off condition': row.equipment_off_condition || '',
                    'Cause (Error Code)': row.cause_error_code || ''
                  };

                  if (!loadedKeys.includes(compositeKey)) {
                    loadedKeys.push(compositeKey);
                  }
                }
              });
            }
          }

          // Sort by date, then by ID (for same dates)
          loadedKeys.sort((a, b) => {
            const dateA = a.includes('_') ? a.split('_')[0] : a;
            const dateB = b.includes('_') ? b.split('_')[0] : b;
            if (dateA !== dateB) {
              return dateA.localeCompare(dateB);
            }
            // If same date, sort by ID (second part after underscore)
            const idA = a.includes('_') ? parseInt(a.split('_')[1] || '0') : 0;
            const idB = b.includes('_') ? parseInt(b.split('_')[1] || '0') : 0;
            return idB - idA; // Descending order (newer IDs first)
          });

          if (Object.keys(loadedData).length > 0) {
            // Filter out removed rows before setting data
            const { data: filteredData, rowKeys: filteredKeys } = filterRemovedRows(
              loadedData,
              loadedKeys,
              'BD Details',
              siteCode,
              loadUserId
            );

            BD_DEBUG && console.log('?? Setting loaded data with', filteredKeys.length, 'rows (', loadedKeys.length - filteredKeys.length, 'removed)');
            BD_DEBUG && console.log('?? Sample loaded row:', filteredData[filteredKeys[0]]);

            // Clear justAddedRowRef for saved dates so real-time refresh can resume
            if (justAddedRowRef.current) {
              const savedDate = justAddedRowRef.current;
              const hasSavedRow = filteredKeys.some(key => {
                const keyDate = key.includes('_') ? key.split('_')[0] : key;
                return keyDate === savedDate;
              });
              if (hasSavedRow) {
                BD_DEBUG && console.log(`? Cleared justAddedRowRef after saving date: ${savedDate}`);
                justAddedRowRef.current = null;
              }
            }

            // Replace all data with the reloaded data to ensure saved rows are visible
            setAllData(filteredData, filteredKeys);

            // Also update correctMistakeData so it's available for the popup
            // Get all data from filteredData for correctMistakeData
            const allRows = Object.values(filteredData);
            const mappedData = allRows.map((row: any) => {
              const toValue = (val: any) => {
                if (val === null || val === undefined || val === '') return null;
                return val;
              };

              return {
                id: row.id || undefined,
                date: toValue(row.date || row.entry_date),
                'Budget PR for the month': toValue(row['Budget PR for the month']),
                'Equipment': toValue(row['Equipment']),
                'BD Category': toValue(row['BD Category']),
                'ICR No': toValue(row['ICR No']),
                'Inverter No.': toValue(row['Inverter No.']),
                'BD From': toValue(row['BD From']),
                'BD up  to': toValue(row['BD up  to']),
                'BD Minutes': row['BD Minutes'] !== null && row['BD Minutes'] !== undefined ? String(row['BD Minutes']) : null,
                'BD Hours': row['BD Hours'] !== null && row['BD Hours'] !== undefined ? String(row['BD Hours']) : null,
                'PA BD Hours': row['PA BD Hours'] !== null && row['PA BD Hours'] !== undefined ? String(row['PA BD Hours']) : null,
                'A = DC Capacity of affected equipment (kW)': row['A = DC Capacity of affected equipment (kW)'] !== null && row['A = DC Capacity of affected equipment (kW)'] !== undefined ? String(row['A = DC Capacity of affected equipment (kW)']) : null,
                'B = POA lost during BD (kWh/m2)': row['B = POA lost during BD (kWh/m2)'] !== null && row['B = POA lost during BD (kWh/m2)'] !== undefined ? String(row['B = POA lost during BD (kWh/m2)']) : null,
                'C=A*B': row['C=A*B'] !== null && row['C=A*B'] !== undefined ? String(row['C=A*B']) : null,
                'Generation loss kWh': row['Generation loss kWh'] !== null && row['Generation loss kWh'] !== undefined ? String(row['Generation loss kWh']) : null,
                "SCB's /QS/UNIT": toValue(row["SCB's /QS/UNIT"]),
                'BD Description': toValue(row['BD Description']),
                'Corrective Action Taken': toValue(row['Corrective Action Taken']),
                'Current Status': toValue(row['Current Status']),
                'Effective loss of POA': toValue(row['Effective loss of POA']),
                'Scope': toValue(row['Scope']),
                'Which Equipment is in off condition': toValue(row['Which Equipment is in off condition']),
                'Cause (Error Code)': toValue(row['Cause (Error Code)']),
                entry_date: row.date || row.entry_date
              };
            });
            setCorrectMistakeData(mappedData);
            BD_DEBUG && console.log('?? Updated correctMistakeData with', mappedData.length, 'rows');
          } else {
            BD_DEBUG && console.warn('?? No data returned after reload');
          }
        }
      }

    } catch (error: any) {
      console.error('? Save error:', error);
      setStatus({
        type: 'error',
        message: `? Save failed: ${error.message}`
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Export data to Excel
   */
  const exportData = async () => {
    setIsExporting(true);

    try {
      // Save any unsaved changes first
      await save();

      // Small delay to ensure database updates are committed
      await new Promise(resolve => setTimeout(resolve, 500));

      // Extract dates from composite keys (format: "date_id" or just "date")
      const extractDate = (key: string): string => {
        if (key.includes('_')) {
          return key.split('_')[0];
        }
        return key;
      };

      const dates = [...new Set(rowKeys.map(key => extractDate(key)))].filter(date => {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        return dateRegex.test(date);
      });

      const result = await bdDetailsService.export(dates);

      if (result.ok) {
        setStatus({ type: 'success', message: '? Export completed successfully!' });
      } else {
        setStatus({ type: 'error', message: `? Export failed: ${result.error}` });
      }
    } catch (error: any) {
      console.error('? Export error:', error);
      setStatus({ type: 'error', message: `? Export failed: ${error.message}` });
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Fetch and show summary modal
   */
  const fetchSummary = async () => {
    setLoadingSummary(true);

    try {
      const res = await bdDetailsService.loadAll();

      if (res.ok && res.data) {
        setSummaryData(res.data);
        setShowSummary(true);
      } else {
        setStatus({ type: 'error', message: `? Failed to load summary: ${res.error}` });
      }
    } catch (error: any) {
      console.error('? Summary fetch error:', error);
      setStatus({ type: 'error', message: `? Failed to load summary: ${error.message}` });
    } finally {
      setLoadingSummary(false);
    }
  };

  // Fetch user's data for Correct Mistake
  // Use already loaded data for Correct Mistake (optimization - no backend call)
  const handleCorrectMistake = () => {
    try {
      // Check if we have any data loaded
      if (rowKeys.length === 0) {
        setStatus({ type: 'warning', message: 'No data available to edit. Please add some data first.' });
        return;
      }

      // Convert current table data to array format for modal
      const dataArray = rowKeys.map(key => ({
        ...data[key],
        entry_date: data[key]?.date || data[key]?.entry_date || key.split('_')[0],
        id: data[key]?.id || undefined
      }));

      BD_DEBUG && console.log('?? Using loaded BD data for edit:', dataArray.length, 'rows');
      setCorrectMistakeData(dataArray);
      setShowCorrectMistake(true);
    } catch (error: any) {
      setStatus({ type: 'error', message: `Error: ${error.message}` });
    }
  };

  // Save corrected data - update all rows that have IDs
  const handleSaveCorrectedData = async (updatedData: any[]) => {
    if (updatedData.length === 0) {
      throw new Error('No data to save');
    }

    const results: { success: number; failed: number; errors: string[] } = {
      success: 0,
      failed: 0,
      errors: []
    };

    // Process all rows that have IDs
    for (const row of updatedData) {
      if (row.id && typeof row.id === 'number') {
        try {
          // Map UI column keys back to backend field names
          const entryDate = row.entry_date || row.date;
          const backendData: BDDetailsDataRow = {
            id: row.id,
            entry_date: entryDate,
            site_code: siteCode,
            budget_pr_for_month: row['Budget PR for the month'] || null,
            equipment: row['Equipment'] || null,
            bd_category: row['BD Category'] || null,
            icr_no: row['ICR No'] || null,
            inverter_no: row['Inverter No.'] || null,
            bd_from: row['BD From'] || null,
            bd_up_to: row['BD up  to'] || null,
            bd_minutes: row['BD Minutes'] ? parseFloat(row['BD Minutes']) : null,
            bd_hours: row['BD Hours'] ? parseFloat(row['BD Hours']) : null,
            pa_bd_hours: row['PA BD Hours'] ? parseFloat(row['PA BD Hours']) : null,
            dc_capacity_kw: row['A = DC Capacity of affected equipment (kW)'] ? parseFloat(row['A = DC Capacity of affected equipment (kW)']) : null,
            poa_lost_kwh_per_m2: row['B = POA lost during BD (kWh/m2)'] ? parseFloat(row['B = POA lost during BD (kWh/m2)']) : null,
            c_a_times_b: row['C=A*B'] ? parseFloat(row['C=A*B']) : null,
            generation_loss_kwh: row['Generation loss kWh'] ? parseFloat(row['Generation loss kWh']) : null,
            scb_qs_unit: row["SCB's /QS/UNIT"] || null,
            bd_description: row['BD Description'] || null,
            corrective_action_taken: row['Corrective Action Taken'] || null,
            current_status: row['Current Status'] || null,
            effective_loss_of_poa: row['Effective loss of POA'] || null,
            scope: row['Scope'] || null,
            equipment_off_condition: row['Which Equipment is in off condition'] || null,
            cause_error_code: row['Cause (Error Code)'] || null
          };

          BD_DEBUG && console.log(`?? Updating BD Details record with ID: ${row.id}`);
          const updateResult = await bdDetailsService.update(row.id, backendData);
          if (updateResult.ok) {
            results.success++;
            BD_DEBUG && console.log(`? Updated BD Details data for ${entryDate} (ID: ${row.id})`);
          } else {
            results.failed++;
            const errorMsg = `Row ${row.id} (${entryDate}): ${updateResult.error || 'Update failed'}`;
            results.errors.push(errorMsg);
            console.error(`? ${errorMsg}`);
          }
        } catch (error: any) {
          results.failed++;
          const errorMsg = `Row ${row.id} (${row.entry_date || row.date}): ${error.message}`;
          results.errors.push(errorMsg);
          console.error(`? ${errorMsg}`);
        }
      } else {
        BD_DEBUG && console.log(`?? Skipping row without ID: ${row.entry_date || row.date}`);
      }
    }

    // Throw error with detailed message if any failures occurred
    if (results.failed > 0) {
      const errorMessage = results.errors.length > 0
        ? `Failed to update ${results.failed} record(s). Errors: ${results.errors.join('; ')}`
        : `Failed to update ${results.failed} record(s).`;
      throw new Error(errorMessage);
    }

    if (results.success === 0) {
      throw new Error('No records were updated. Please check that rows have valid IDs.');
    }

    // Reload data after successful corrections to update the table display
    if (results.success > 0) {
      BD_DEBUG && console.log('?? Reloading data after corrections...');
      try {
        // Small delay to ensure database updates are committed
        await new Promise(resolve => setTimeout(resolve, 300));

        // Get current user info
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user?.id) {
          const backendUser = userResponse.user;
          const resolvedUserId = resolveUserFilterId(user, backendUser);
          const loadUserId = (resolvedUserId ?? Number(backendUser.id)) || 0;
          const isAdmin = shouldLoadAllRecords(user, backendUser);

          let loadedData: any = {};
          let loadedKeys: string[] = [];

          if (isAdmin) {
            // Admin: Load all data for the site using get-dgr endpoint
            try {
              const API_BASE_URL = 'http://localhost:8000/api/v1';
              const cacheBuster = `_t=${Date.now()}`;
              const token = sessionStorage.getItem('access_token');
              const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=BD&${cacheBuster}`, {
                cache: 'no-cache',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Cache-Control': 'no-cache',
                  'Pragma': 'no-cache'
                }
              });
              if (response.ok) {
                const payload = await response.json();
                const bdData = payload?.data?.bd || [];

                bdData.forEach((row: any) => {
                  const entryDate = row.entry_date;
                  const rowId = row.id || row.pk;

                  if (entryDate) {
                    // Use composite key: entry_date_id to handle multiple rows per date
                    const compositeKey = rowId ? `${entryDate}_${rowId}` : entryDate;

                    loadedData[compositeKey] = {
                      id: rowId, // Store ID for updates
                      date: entryDate, // Store actual date for display
                      'Budget PR for the month': row.budget_pr_for_month || getCurrentMonthPR(),
                      'Equipment': row.equipment || '',
                      'BD Category': row.bd_category || '',
                      'ICR No': row.icr_no || '',
                      'Inverter No.': row.inverter_no || '',
                      'BD From': row.bd_from || '',
                      'BD up  to': row.bd_up_to || '',
                      'BD Minutes': row.bd_minutes !== null && row.bd_minutes !== undefined ? String(row.bd_minutes) : '',
                      'BD Hours': row.bd_hours !== null && row.bd_hours !== undefined ? String(row.bd_hours) : '',
                      'PA BD Hours': row.pa_bd_hours !== null && row.pa_bd_hours !== undefined ? String(row.pa_bd_hours) : '',
                      'A = DC Capacity of affected equipment (kW)': row.dc_capacity_kw !== null && row.dc_capacity_kw !== undefined ? String(row.dc_capacity_kw) : '',
                      'B = POA lost during BD (kWh/m2)': row.poa_lost_kwh_per_m2 !== null && row.poa_lost_kwh_per_m2 !== undefined ? String(row.poa_lost_kwh_per_m2) : '',
                      'C=A*B': row.c_a_times_b !== null && row.c_a_times_b !== undefined ? String(row.c_a_times_b) : '',
                      'Generation loss kWh': row.generation_loss_kwh !== null && row.generation_loss_kwh !== undefined ? String(row.generation_loss_kwh) : '',
                      "SCB's /QS/UNIT": row.scb_qs_unit || '',
                      'BD Description': row.bd_description || '',
                      'Corrective Action Taken': row.corrective_action_taken || '',
                      'Current Status': row.current_status || '',
                      'Effective loss of POA': row.effective_loss_of_poa || '',
                      'Scope': row.scope || '',
                      'Which Equipment is in off condition': row.equipment_off_condition || '',
                      'Cause (Error Code)': row.cause_error_code || ''
                    };

                    if (!loadedKeys.includes(compositeKey)) {
                      loadedKeys.push(compositeKey);
                    }
                  }
                });
              }
            } catch (error) {
              console.error('Error loading all data via get-dgr:', error);
              // Fallback to loadByUser if get-dgr fails
              const loadResult = await bdDetailsService.loadByUser(loadUserId);
              if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
                loadResult.data.forEach((row: any) => {
                  const entryDate = row.entry_date;
                  const rowId = row.id || row.pk;

                  if (entryDate) {
                    const compositeKey = rowId ? `${entryDate}_${rowId}` : entryDate;

                    loadedData[compositeKey] = {
                      id: rowId,
                      date: entryDate,
                      'Budget PR for the month': row.budget_pr_for_month || getCurrentMonthPR(),
                      'Equipment': row.equipment || '',
                      'BD Category': row.bd_category || '',
                      'ICR No': row.icr_no || '',
                      'Inverter No.': row.inverter_no || '',
                      'BD From': row.bd_from || '',
                      'BD up  to': row.bd_up_to || '',
                      'BD Minutes': row.bd_minutes !== null && row.bd_minutes !== undefined ? String(row.bd_minutes) : '',
                      'BD Hours': row.bd_hours !== null && row.bd_hours !== undefined ? String(row.bd_hours) : '',
                      'PA BD Hours': row.pa_bd_hours !== null && row.pa_bd_hours !== undefined ? String(row.pa_bd_hours) : '',
                      'A = DC Capacity of affected equipment (kW)': row.dc_capacity_kw !== null && row.dc_capacity_kw !== undefined ? String(row.dc_capacity_kw) : '',
                      'B = POA lost during BD (kWh/m2)': row.poa_lost_kwh_per_m2 !== null && row.poa_lost_kwh_per_m2 !== undefined ? String(row.poa_lost_kwh_per_m2) : '',
                      'C=A*B': row.c_a_times_b !== null && row.c_a_times_b !== undefined ? String(row.c_a_times_b) : '',
                      'Generation loss kWh': row.generation_loss_kwh !== null && row.generation_loss_kwh !== undefined ? String(row.generation_loss_kwh) : '',
                      "SCB's /QS/UNIT": row.scb_qs_unit || '',
                      'BD Description': row.bd_description || '',
                      'Corrective Action Taken': row.corrective_action_taken || '',
                      'Current Status': row.current_status || '',
                      'Effective loss of POA': row.effective_loss_of_poa || '',
                      'Scope': row.scope || '',
                      'Which Equipment is in off condition': row.equipment_off_condition || '',
                      'Cause (Error Code)': row.cause_error_code || ''
                    };

                    if (!loadedKeys.includes(compositeKey)) {
                      loadedKeys.push(compositeKey);
                    }
                  }
                });
              }
            }
          } else {
            // Regular user: Load only their data
            const loadResult = await bdDetailsService.loadByUser(loadUserId);
            if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
              loadResult.data.forEach((row: any) => {
                const entryDate = row.entry_date;
                const rowId = row.id || row.pk;

                if (entryDate) {
                  const compositeKey = rowId ? `${entryDate}_${rowId}` : entryDate;

                  loadedData[compositeKey] = {
                    id: rowId,
                    date: entryDate,
                    'Budget PR for the month': row.budget_pr_for_month || getCurrentMonthPR(),
                    'Equipment': row.equipment || '',
                    'BD Category': row.bd_category || '',
                    'ICR No': row.icr_no || '',
                    'Inverter No.': row.inverter_no || '',
                    'BD From': row.bd_from || '',
                    'BD up  to': row.bd_up_to || '',
                    'BD Minutes': row.bd_minutes !== null && row.bd_minutes !== undefined ? String(row.bd_minutes) : '',
                    'BD Hours': row.bd_hours !== null && row.bd_hours !== undefined ? String(row.bd_hours) : '',
                    'PA BD Hours': row.pa_bd_hours !== null && row.pa_bd_hours !== undefined ? String(row.pa_bd_hours) : '',
                    'A = DC Capacity of affected equipment (kW)': row.dc_capacity_kw !== null && row.dc_capacity_kw !== undefined ? String(row.dc_capacity_kw) : '',
                    'B = POA lost during BD (kWh/m2)': row.poa_lost_kwh_per_m2 !== null && row.poa_lost_kwh_per_m2 !== undefined ? String(row.poa_lost_kwh_per_m2) : '',
                    'C=A*B': row.c_a_times_b !== null && row.c_a_times_b !== undefined ? String(row.c_a_times_b) : '',
                    'Generation loss kWh': row.generation_loss_kwh !== null && row.generation_loss_kwh !== undefined ? String(row.generation_loss_kwh) : '',
                    "SCB's /QS/UNIT": row.scb_qs_unit || '',
                    'BD Description': row.bd_description || '',
                    'Corrective Action Taken': row.corrective_action_taken || '',
                    'Current Status': row.current_status || '',
                    'Effective loss of POA': row.effective_loss_of_poa || '',
                    'Scope': row.scope || '',
                    'Which Equipment is in off condition': row.equipment_off_condition || '',
                    'Cause (Error Code)': row.cause_error_code || ''
                  };

                  if (!loadedKeys.includes(compositeKey)) {
                    loadedKeys.push(compositeKey);
                  }
                }
              });
            }
          }

          // Sort by date, then by ID (for same dates)
          loadedKeys.sort((a, b) => {
            const dateA = a.includes('_') ? a.split('_')[0] : a;
            const dateB = b.includes('_') ? b.split('_')[0] : b;
            if (dateA !== dateB) {
              return dateA.localeCompare(dateB);
            }
            // If same date, sort by ID (second part after underscore)
            const idA = a.includes('_') ? parseInt(a.split('_')[1] || '0') : 0;
            const idB = b.includes('_') ? parseInt(b.split('_')[1] || '0') : 0;
            return idB - idA; // Descending order (newer IDs first)
          });

          if (Object.keys(loadedData).length > 0) {
            // Filter out removed rows before setting data
            const { data: filteredData, rowKeys: filteredKeys } = filterRemovedRows(
              loadedData,
              loadedKeys,
              'BD Details',
              siteCode,
              loadUserId
            );

            BD_DEBUG && console.log('?? Setting reloaded data with', filteredKeys.length, 'rows after corrections (', loadedKeys.length - filteredKeys.length, 'removed)');
            // Replace all data with the reloaded data to show corrected values
            setAllData(filteredData, filteredKeys);

            // Also update correctMistakeData so it's available for the popup
            const allRows = Object.values(filteredData);
            const mappedData = allRows.map((row: any) => {
              const toValue = (val: any) => {
                if (val === null || val === undefined || val === '') return null;
                return val;
              };

              return {
                id: row.id || undefined,
                date: toValue(row.date || row.entry_date),
                'Budget PR for the month': toValue(row['Budget PR for the month']),
                'Equipment': toValue(row['Equipment']),
                'BD Category': toValue(row['BD Category']),
                'ICR No': toValue(row['ICR No']),
                'Inverter No.': toValue(row['Inverter No.']),
                'BD From': toValue(row['BD From']),
                'BD up  to': toValue(row['BD up  to']),
                'BD Minutes': row['BD Minutes'] !== null && row['BD Minutes'] !== undefined ? String(row['BD Minutes']) : null,
                'BD Hours': row['BD Hours'] !== null && row['BD Hours'] !== undefined ? String(row['BD Hours']) : null,
                'PA BD Hours': row['PA BD Hours'] !== null && row['PA BD Hours'] !== undefined ? String(row['PA BD Hours']) : null,
                'A = DC Capacity of affected equipment (kW)': row['A = DC Capacity of affected equipment (kW)'] !== null && row['A = DC Capacity of affected equipment (kW)'] !== undefined ? String(row['A = DC Capacity of affected equipment (kW)']) : null,
                'B = POA lost during BD (kWh/m2)': row['B = POA lost during BD (kWh/m2)'] !== null && row['B = POA lost during BD (kWh/m2)'] !== undefined ? String(row['B = POA lost during BD (kWh/m2)']) : null,
                'C=A*B': row['C=A*B'] !== null && row['C=A*B'] !== undefined ? String(row['C=A*B']) : null,
                'Generation loss kWh': row['Generation loss kWh'] !== null && row['Generation loss kWh'] !== undefined ? String(row['Generation loss kWh']) : null,
                "SCB's /QS/UNIT": toValue(row["SCB's /QS/UNIT"]),
                'BD Description': toValue(row['BD Description']),
                'Corrective Action Taken': toValue(row['Corrective Action Taken']),
                'Current Status': toValue(row['Current Status']),
                'Effective loss of POA': toValue(row['Effective loss of POA']),
                'Scope': toValue(row['Scope']),
                'Which Equipment is in off condition': toValue(row['Which Equipment is in off condition']),
                'Cause (Error Code)': toValue(row['Cause (Error Code)']),
                entry_date: row.date || row.entry_date
              };
            });
            setCorrectMistakeData(mappedData);
            BD_DEBUG && console.log('?? Updated table and correctMistakeData with corrected values');
          }
        }
      } catch (reloadError: any) {
        console.error('?? Error reloading data after corrections:', reloadError);
        // Don't throw - corrections were saved successfully, just couldn't reload
        // The user can manually refresh if needed
      }
    }
  };

  /**
   * Handle row deletion - shows dialog for permanent vs temporary removal
   */
  const handleRemoveRow = (index: number) => {
    const keyToRemove = rowKeys[index];
    setRowToDelete({ index, key: keyToRemove });
    setShowDeleteDialog(true);
  };

  /**
   * Handle permanent deletion - delete from database
   */
  const handlePermanentDelete = async () => {
    if (!rowToDelete) return;

    const keyToRemove = rowToDelete.key;
    const rowData = data[keyToRemove];
    const rowId = rowData?.id;

    if (!rowId) {
      // If it has no ID, it's just a local unsaved row. We can just remove it from frontend state.
      const updatedData = { ...data };
      delete updatedData[keyToRemove];
      const updatedKeys = rowKeys.filter(k => k !== keyToRemove).sort((a, b) => {
        const dateA = a.includes('_') ? a.split('_')[0] : a;
        const dateB = b.includes('_') ? b.split('_')[0] : b;
        if (dateA !== dateB) {
          return dateA.localeCompare(dateB);
        }
        const idA = a.includes('_') ? parseInt(a.split('_')[1] || '0') : 0;
        const idB = b.includes('_') ? parseInt(b.split('_')[1] || '0') : 0;
        return idB - idA;
      });

      setAllData(updatedData, updatedKeys);
      
      setStatus({
        type: 'success',
        message: 'Unsaved row removed'
      });
      setShowDeleteDialog(false);
      setRowToDelete(null);
      return;
    }

    try {
      setIsSaving(true);
      const deleteResult = await bdDetailsService.delete(rowId);

      if (deleteResult.ok) {
        // Remove from removed rows list if it was there
        backendApi.getCurrentUser().then(response => {
          if (response.success && response.user?.id) {
            const backendUser = response.user;
            const resolvedUserId = resolveUserFilterId(user, backendUser);
            const userIdForStore = resolvedUserId ?? Number(backendUser.id);
            removeRemovedRow('BD Details', siteCode, keyToRemove, userIdForStore);
          } else {
            removeRemovedRow('BD Details', siteCode, keyToRemove);
          }
        }).catch(() => {
          removeRemovedRow('BD Details', siteCode, keyToRemove);
        });

        // Remove the row from frontend state
        const updatedData = { ...data };
        delete updatedData[keyToRemove];
        const updatedKeys = rowKeys.filter(k => k !== keyToRemove).sort((a, b) => {
          const dateA = a.includes('_') ? a.split('_')[0] : a;
          const dateB = b.includes('_') ? b.split('_')[0] : b;
          if (dateA !== dateB) {
            return dateA.localeCompare(dateB);
          }
          const idA = a.includes('_') ? parseInt(a.split('_')[1] || '0') : 0;
          const idB = b.includes('_') ? parseInt(b.split('_')[1] || '0') : 0;
          return idB - idA;
        });

        setAllData(updatedData, updatedKeys);

        const dateFromKey = keyToRemove.includes('_') ? keyToRemove.split('_')[0] : keyToRemove;
        setStatus({
          type: 'success',
          message: `Permanently deleted ${dateFromKey} from database`
        });

        if (updatedKeys.length === 0) {
          setAllData({}, []);
        }
      } else {
        setStatus({
          type: 'error',
          message: `Failed to delete: ${deleteResult.error || 'Unknown error'}`
        });
      }
    } catch (error: any) {
      setStatus({
        type: 'error',
        message: `Failed to delete: ${error.message || 'Unknown error'}`
      });
    } finally {
      setIsSaving(false);
      setRowToDelete(null);
    }
  };

  /**
   * Handle temporary removal - hide until page refresh
   */
  const handleTemporaryRemove = () => {
    if (!rowToDelete) return;

    const keyToRemove = rowToDelete.key;

    // Add to removed rows list (persists across sheet switches)
    backendApi.getCurrentUser().then(response => {
      if (response.success && response.user?.id) {
        const backendUser = response.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForStore = resolvedUserId ?? Number(backendUser.id);
        addRemovedRow('BD Details', siteCode, keyToRemove, userIdForStore);
      } else {
        addRemovedRow('BD Details', siteCode, keyToRemove);
      }
    }).catch(() => {
      addRemovedRow('BD Details', siteCode, keyToRemove);
    });

    // Remove the row from frontend state only
    const updatedData = { ...data };
    delete updatedData[keyToRemove];
    const updatedKeys = rowKeys.filter(k => k !== keyToRemove).sort((a, b) => {
      const dateA = a.includes('_') ? a.split('_')[0] : a;
      const dateB = b.includes('_') ? b.split('_')[0] : b;
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }
      const idA = a.includes('_') ? parseInt(a.split('_')[1] || '0') : 0;
      const idB = b.includes('_') ? parseInt(b.split('_')[1] || '0') : 0;
      return idB - idA;
    });

    setAllData(updatedData, updatedKeys);

    const dateFromKey = keyToRemove.includes('_') ? keyToRemove.split('_')[0] : keyToRemove;
    setStatus({
      type: 'info',
      message: `Temporarily hidden ${dateFromKey} (will reappear after page refresh)`
    });

    if (updatedKeys.length === 0) {
      setAllData({}, []);
    }

    setRowToDelete(null);
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden relative">
      <div className="w-full h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div
          className="text-white px-3 pt-2 pb-0"
          style={{ background: 'linear-gradient(to right, var(--color-primary-blue), var(--color-secondary-tata-blue))' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText size={16} />
              <div>
                <h1 className="text-sm font-bold" style={{ color: '#ffffff', fontWeight: 700 }}><strong>Breakdowns</strong></h1>
                <p className="text-xs" style={{ color: '#ffffff', opacity: 0.95 }}>{siteName} - Breakdown Details</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* DEBUG: Manual load button - COMMENTED OUT per user request */}
              {/* {!isInitialLoad && hasMoreDataRef.current && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    BD_DEBUG && console.log(`?? [TEST] Manual load button clicked`);
                    BD_DEBUG && console.log(`?? [TEST] hasMoreData: ${hasMoreDataRef.current}, oldestDate: ${cursorDateRef.current}`);
                    BD_DEBUG && console.log(`?? [TEST] containerRef.current:`, containerRef.current);
                    BD_DEBUG && console.log(`?? [TEST] scrollTop:`, containerRef.current?.scrollTop);
                    loadMoreOlderData();
                  }}
                  className="flex items-center gap-1 px-3 py-1 rounded text-xs text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'var(--color-secondary-orange)' }}
                  title="Load Older Data"
                  disabled={isLoadingOlder}
                >
                  {isLoadingOlder ? (
                    <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                  ) : (
                    <ChevronUp size={12} />
                  )}
                  <span className="hidden sm:inline">Load Older</span>
                </motion.button>
              )} */}
              <FormActions
                isCondensed={isCondensed}
                onToggleView={() => setIsCondensed(!isCondensed)}
                isExporting={isExporting}
                isSaving={isSaving}
                loadingSummary={loadingSummary}
                hasData={true}
                onAddRow={handleAddRow}
                onShowSummary={fetchSummary}
                onSave={save}
                onExport={exportData}
                addRowLabel="New Date"
                customActions={
                  <>
                    <DateRangeFilter
                      startDate={startDate}
                      endDate={endDate}
                      onDateChange={(start, end) => {
                        if (start === null && end === null && (startDate !== null || endDate !== null)) {
                          window.location.reload();
                        } else {
                          setStartDate(start);
                          setEndDate(end);
                        }
                      }}
                    />
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleCorrectMistake}
                      disabled={loadingCorrectMistake}
                      className="flex items-center gap-1 px-3 py-1 rounded text-xs text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: 'var(--color-secondary-orange)' }}
                      title="Edit previously entered data"
                    >
                      {loadingCorrectMistake ? (
                        <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                      ) : (
                        <Edit size={12} />
                      )}
                      <span className="hidden sm:inline">Edit</span>
                    </motion.button>
                  </>
                }
              />
            </div>
          </div>
        </div>

        {/* Date Filter Loading Indicator */}
        {isDateFilterLoading && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-[#0066A3] text-white px-4 py-3 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center gap-3">
              <div className="animate-spin h-5 w-5 border-3 border-white border-t-transparent rounded-full"></div>
              <div className="flex-1">
                <div className="font-semibold">{dateFilterProgress || 'Loading data...'}</div>
                <div className="text-xs opacity-90">Please wait while we fetch your data from the server</div>
              </div>
            </div>
          </div>
        )}

        {/* Status Banner */}
        {status.type && (
          <StatusBanner
            status={status.type}
            message={status.message}
            onClose={() => setStatus({ type: null, message: '' })}
          />
        )}

        {/* Table */}
        <div
          className="flex-1 min-h-0 flex flex-col"
          style={{
            position: 'relative',
            scrollBehavior: 'auto', // Disable smooth scrolling to prevent animation
            opacity: isTableVisible ? 1 : 0,
            transition: 'opacity 0.15s ease-in-out'
          }}
        >
          <UniversalTable
            topRowContent={
              hasMoreData && !startDate && !endDate ? (
                <div className="flex items-center justify-center w-full h-full p-0.5 cursor-pointer" style={{ cursor: 'pointer' }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      loadMoreOlderData();
                    }}
                    disabled={isLoadingOlder}
                    className="w-6 h-6 bg-[#0066A3] hover:bg-[#1C4D88] active:scale-95 text-white rounded flex items-center justify-center cursor-pointer shadow-md transition-all duration-150 border border-[#3EA3DA] disabled:opacity-50"
                    style={{ zIndex: 100, pointerEvents: 'auto', cursor: 'pointer' }}
                    title="Load More Older Data"
                  >
                    {isLoadingOlder ? (
                      <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <Plus size={16} color="#ffffff" strokeWidth={3} className="text-white" />
                    )}
                  </button>
                </div>
              ) : undefined
            }
            columns={dynamicColumns}
            data={data}
            rowKeys={(startDate || endDate) ? filteredRowKeys : memoizedRowKeys}
            styleConfig={isCondensed ? DEFAULT_STYLE_CONFIG : EXPANDED_STYLE_CONFIG}
            focusedCell={focusedCell}
            onCellChange={handleCellChange}
            onRowKeyChange={handleRowKeyChange}
            onRemoveRowKey={handleRemoveRow}
            onKeyDown={handleKeyDown}
            onFocus={handleTableFocus}
            setInputRef={registerInput}
            containerRef={containerRef}
            loading={isInitialLoad}
          />
        </div>

        {/* Summary Modal */}
        {showSummary && (
          <SummaryModal
            isOpen={showSummary}
            onClose={() => setShowSummary(false)}
            title="Breakdowns Summary - axon"
            data={summaryData}
            columns={[
              { key: 'entry_date', label: 'Date' },
              { key: 'equipment', label: 'Equipment' },
              { key: 'bd_category', label: 'Category' },
              { key: 'bd_hours', label: 'BD Hours', format: (v) => v ? Number(v).toFixed(2) : '-' },
              { key: 'generation_loss_kwh', label: 'Gen Loss (kWh)', format: (v) => v ? Number(v).toFixed(2) : '-' },
              { key: 'current_status', label: 'Status' }
            ]}
          />
        )}

        {/* Correct Mistake Modal */}
        {showCorrectMistake && (
          <CorrectMistakeModal
            isOpen={showCorrectMistake}
            onClose={() => setShowCorrectMistake(false)}
            title="Edit - Breakdowns"
            data={correctMistakeData}
            columns={modalColumns}
            onSave={handleSaveCorrectedData}
            isLoading={loadingCorrectMistake}
            siteCode="axon"
          />
        )}

        {/* Footer */}
        <div className="px-4 py-2 text-xs border-t flex items-center justify-between" style={{ background: 'var(--color-gray-100)', color: '#000000', fontWeight: 400 }}>
          <span>{footerText}</span>
        </div>
      </div>

      {/* Delete Row Dialog */}
      <DeleteRowDialog
        isOpen={showDeleteDialog}
        rowKey={rowToDelete?.key || ''}
        onClose={() => {
          setShowDeleteDialog(false);
          setRowToDelete(null);
        }}
        onPermanent={handlePermanentDelete}
        onTemporary={handleTemporaryRemove}
      />
    </div>
  );
};


